<template>
  <div
    :data-auto-test-inital-data-loaded="initialDataAreLoaded"
    class="krf-KeyResultForm"
    data-auto-testid="kr-form"
  >
    <portal :to="portalName">
      <ParentElementSelect
        v-model:contribute="formModel.contribute"
        v-model:parent-id="formModel.parentId"
        :disable-on-edit="disableOnEdit"
        :has-unlink-action="isShowUnlinkParentButton"
        :is-edit="isEdit"
        :is-element-closed="isOkrElementClosed"
        :is-error="isShowParentRequiredError"
        :loading="isParentObjectivesLoading"
        :options="getObjectivesAsOptions"
        :parent-element-readable="parentElementReadable"
        :search-function="getParentObjectives"
        :selected-parent-element="getCurrentParentElement"
        data-auto-testid="parent-field"
        data-testid="parent-field"
        @edit-element="onParentObjectiveIdClick($event.item)"
        @update:options="parentObjectives = $event"
        @unlink-parent="unlinkParentElement"
        @update:parent-id="onParentObjectiveUpdate"
        @update:contribute="onContributeChange"
      />
    </portal>

    <SidebarLayout
      ref="sidebarLayout"
      :update-scrollbar-triggers="updateScrollbarTriggers"
      scroll-to-top-on-scrollbar-update
    >
      <LockMessage
        v-if="lockedMessagesStates.isShowNoPermissionsMessage"
        :type="LOCK_MESSAGE_TYPES.WARNING"
        class="krf-NoPermissionsMessage"
      >
        {{ $t('lock_message', { element: $t('okr_elements.metric') }) }}
      </LockMessage>

      <LockMessage
        v-if="lockedMessagesStates.isShowClosedElementMessage"
        :type="LOCK_MESSAGE_TYPES.INFO"
        class="krf-ClosedStatusMessage"
        icon-name="info-next"
      >
        <span>
          {{ $t('lock_message.grade_closed', { status: getOkrElementClosedStatusName }) }}
          <br />
          <span class="krf-ClosedStatusMessage_Description">
            {{ $t('lock_message.grade_closed.description') }}
          </span>
        </span>
      </LockMessage>

      <OkrTitleFiled
        :ref="elementFormReferences.title"
        v-model="formModel.name"
        :is-error="isTitleEmpty"
        :placeholder="$t('common.title')"
        :readonly="isFieldDisabled || isOkrElementClosed"
        class="krf-Title"
        data-auto-testid="name-field"
        data-testid="name-field"
        @blur="onNameUpdate"
        @update:model-value="isTitleEmpty = $event === ''"
      />
      <DescriptionField
        v-if="isOkrElementClosed ? formModel.description : true"
        ref="description"
        v-model="formModel.description"
        :actions="editable"
        :max-length="DESCRIPTION_FILED_LIMITS.DESCRIPTION"
        :placeholder="$t('description.placeholder')"
        :readonly="disableOnEdit"
        :show-placeholder="!disableOnEdit"
        :workspace-id="formModel.workspaceId"
        data-auto-testid="description-field"
        data-testid="description-field"
        @cancel="cancelDescriptionEditing"
        @save="saveDescription"
      />

      <LockMessage
        v-if="lockedMessagesStates.isShowClosedParentMessage"
        :type="LOCK_MESSAGE_TYPES.WARNING"
        class="krf-LockMessage"
        icon-name="warning-icon"
      >
        <i18n-t keypath="lock_message.entity_parent_closed" scope="global">
          <template #parentOkr>
            {{ getParentOkrTypeName }}
          </template>
          <template #statusName> {{ getParentElementClosedStatusName }}</template>
        </i18n-t>
      </LockMessage>

      <LockMessage
        v-if="isShowProgressLockMessage"
        :type="LOCK_MESSAGE_TYPES.INFO"
        class="krf-LockMessage"
      >
        <div>
          {{ $t('update.keyResult.result_type_lock_message') }}
          <i18n-t
            class="krf-ResultTypeWrapper_LockMessageDescription"
            keypath="update.keyResult.result_type_lock_message_description"
            scope="global"
            tag="div"
          >
            <template #action>
              <template v-if="okrWeightsLimitations.isDisabled({ okrElement: modelValue })">
                {{ $t('update.keyResult.result_type_lock_message_description_action') }}
              </template>
              <a v-else href="#" @click.prevent="$emit('open-custom-weights', modelValue)">
                {{ $t('update.keyResult.result_type_lock_message_description_action') }}
              </a>
            </template>
          </i18n-t>
        </div>
      </LockMessage>

      <div
        :class="{
          'krf-ResultTypeWrapper-binary': isBinary
        }"
        class="krf-ResultTypeWrapper"
      >
        <AppTabs
          v-model="currentProgressAdjustmentTab"
          :options="progressAdjustmentTabs"
          hide-nav-permanently
        >
          <template #[chartTabName]="{ isTabAvailable, activateTab }">
            <LockMessage v-if="isInBacklogInterval && !showChart" :type="LOCK_MESSAGE_TYPES.INFO">
              {{ $t('objective.chart.backlog_message') }}
            </LockMessage>

            <ElementProgressChart
              v-if="showChart && modelValue.uniqueId"
              ref="objectiveChart"
              :disable-on-edit="disableOnEdit"
              :element="modelValue"
              :element-id="modelValue.uniqueId"
              :element-target-periods="
                milestonesSettings[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS] ||
                DEFAULT_MILESTONES_SETTINGS[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS]
              "
              :show-table="isShowChartTable"
              :workspace-id="formModel.workspaceId"
              class="krf-Progress"
              data-auto-testid="element-progress-wrapper"
              data-testid="element-progress-wrapper"
              @value-updated="onProgressValueUpdated"
              @threshold-updated="onPeriodThresholdUpdated"
              @open-checkin-modal="openCheckinModal"
              @update-element="updateElementAndDependents"
            >
              <template
                v-if="isTabAvailable(ELEMENT_PROGRESS_ADJUSTMENT_TABS.SETUP.value)"
                #actions-append
              >
                <AppButton
                  v-tippy="{
                    placement: 'top',
                    content: $t(ELEMENT_PROGRESS_ADJUSTMENT_TABS.SETUP.label)
                  }"
                  :data-auto-testid="ELEMENT_PROGRESS_SETUP_BUTTON_TEST_ID"
                  :data-testid="ELEMENT_PROGRESS_SETUP_BUTTON_TEST_ID"
                  :size="BUTTON_SIZES.SM"
                  class="krf-Progress_NavButton"
                  height="24"
                  icon="table-settings-next"
                  type="ghost-next"
                  width="24"
                  @click="activateTab(ELEMENT_PROGRESS_ADJUSTMENT_TABS.SETUP.value)"
                />
              </template>
            </ElementProgressChart>
          </template>

          <template #[setupTabName]="{ isTabAvailable, activateTab }">
            <ResultTypeSettings
              v-if="isShowResultTypeSettings"
              :fields-count="resultTypeSettingsFieldsCount"
              :show-header="isEdit"
              :show-lock-message="isShowProgressLockMessage"
              :type="getResultTypeSettingsType"
            >
              <template
                v-if="isEdit && isTabAvailable(ELEMENT_PROGRESS_ADJUSTMENT_TABS.CHART.value)"
                #header-prepend
              >
                <AppButton
                  v-tippy="{
                    placement: 'top',
                    content: $t(ELEMENT_PROGRESS_ADJUSTMENT_TABS.CHART.label)
                  }"
                  :data-auto-testid="ELEMENT_PROGRESS_SETUP_BACK_BUTTON_TEST_ID"
                  :data-testid="ELEMENT_PROGRESS_SETUP_BACK_BUTTON_TEST_ID"
                  :size="BUTTON_SIZES.SM"
                  icon="arrow-left-next"
                  type="ghost-next"
                  @click="activateTab(ELEMENT_PROGRESS_ADJUSTMENT_TABS.CHART.value)"
                />
              </template>
              <template #lock-message-description>
                <i18n-t
                  class="krf-ResultTypeWrapper_LockMessageDescription"
                  keypath="update.keyResult.result_type_lock_message_description"
                  scope="global"
                  tag="div"
                >
                  <template #action>
                    <template v-if="okrWeightsLimitations.isDisabled({ okrElement: modelValue })">
                      {{ $t('update.keyResult.result_type_lock_message_description_action') }}
                    </template>
                    <a v-else href="#" @click.prevent="$emit('open-custom-weights', modelValue)">
                      {{ $t('update.keyResult.result_type_lock_message_description_action') }}
                    </a>
                  </template>
                </i18n-t>
              </template>
              <template #left-side>
                <AppRadioGroup
                  v-if="!isEdit || !disableOnEdit"
                  :disabled="resultTypeSettingsDisabledStates.select"
                  :model-value="formModel.fieldTypeId"
                  :name="`result-type-${formModel.id || uid}`"
                  :options="RESULT_TYPE_OPTIONS"
                  data-auto-testid="value-type-field"
                  data-testid="value-type-field"
                  style="--option-padding: 0 8px 0 0"
                  @update:model-value="changeResultType"
                >
                  <template #item-label="{ item }">
                    <AppRadioGroupNextOptionWithIcon
                      v-tippy="{
                        ...item.tooltipSettings
                      }"
                      :option="item"
                    />
                  </template>
                </AppRadioGroup>
              </template>

              <template #right-side>
                <!-- key is necessary for automatically update masks on when values changed outside -->
                <ResultTypeSettingsDefaultRightSide
                  :key="resultTypeSettingsUid"
                  v-model:current-value="currentValue"
                  v-model:original-value="originalValue"
                  v-model:target-value="targetValue"
                  v-model:threshold-condition="thresholdCondition"
                  :editable="editable && !isOkrElementClosed"
                  :field-type-id="formModel.fieldTypeId"
                  :form-model="resultTypeSettingsFormModel"
                  :is-binary="isBinary"
                  :is-binary-select-disabled="isBinarySelectDisabled"
                  :is-edit="isEdit"
                  :is-locked-on-percent="isLockedOnPercent"
                  :is-threshold="isThreshold"
                  :unit="formModel.unit"
                  :values-are-readonly="valuesAreReadonly"
                  data-testid="result-type-settings-controls"
                  @update:binary-value="onBinaryValueUpdate"
                  @update:threshold-condition="onThresholdConditionUpdate"
                  @blur-original-value="onOriginalValueUpdate"
                  @blur-current-value="onCurrentValueUpdate"
                  @blur-target-value="onTargetValueUpdate"
                />
              </template>

              <template #append-after-right-side>
                <MetricUnitInput
                  v-if="!isLockedOnPercent && !isBinary"
                  v-model="formModel.unit"
                  :editable="editable && !isOkrElementClosed"
                  :is-binary-select-disabled="isBinarySelectDisabled"
                  :is-edit="isEdit"
                  :is-locked-on-percent="isLockedOnPercent"
                  @blur="onUnitUpdate"
                />
              </template>

              <template #footer>
                <ElementProgressMilestones
                  :ref="elementFormReferences.milestones"
                  :editable="editable && !isOkrElementClosed"
                  :field-type-id="formModel.fieldTypeId"
                  :is-binary-select-disabled="isBinarySelectDisabled"
                  :is-edit="isEdit"
                  :is-locked-on-percent="isLockedOnPercent"
                  :milestones-settings="milestonesSettings"
                  :target-value="targetValue"
                  :unit="formModel.unit"
                  :values-are-readonly="valuesAreReadonly"
                  is-metric
                  @update-milestones-settings="onMilestonesSettingsUpdate"
                  @update-progress-aggregation="onUpdateProgressAggregation"
                />

                <ElementDataSource
                  v-if="isShowElementDataSource"
                  :element="formModel"
                  @update-element="updateElementAndDependents"
                />
              </template>
            </ResultTypeSettings>
          </template>
        </AppTabs>
      </div>

      <FormNestedItemsField
        :is-expand-all-loading="isExpandAllLoading"
        :show-expand-collapse-button="someItemIsExpandable"
        :show-head="childKrs.length > 0"
        :title="$t('kr.nested_items')"
        @expand-all="expandAll"
        @collapse-all="collapseAll"
      >
        <template #actions>
          <WeightsModalTrigger
            v-if="showWeightsModalTrigger"
            v-tippy="{
              content: okrWeightsLimitations.getTooltip({ okrElement: modelValue }),
              placement: 'top',
              theme: `${DROP_LIST_THEMES.TRANSLUCENT_NEXT} ${DROP_LIST_THEMES.PADDING_DEFAULT} ${DROP_LIST_THEMES.TEXT_CENTER} ${DROP_LIST_THEMES.WHITE_SPACE_PRE_LINE}`
            }"
            :disabled="okrWeightsLimitations.isDisabled({ okrElement: modelValue })"
            data-auto-testid="custom-weights-button"
            data-testid="custom-weights-button"
            @click="$emit('open-custom-weights', modelValue)"
          />

          <AddNestedItem
            v-if="userCanCreateObjectives && createNestedElementDropdownItems.length"
            v-tippy="{
              placement: 'top',
              content: $t('kr.add_nested_item'),
              boundary: 'viewport'
            }"
            :dropdown-options="createNestedElementDropdownItems"
            :follow-cursor="false"
            data-auto-testid="add-nested-element"
            simplify
            @on-option-click="onActionMenuItemsClick"
          />
        </template>

        <OkrElementsList
          ref="childKRs"
          :allow-delete-action="!isOkrElementClosed"
          :allow-unlink-action="!isOkrElementClosed"
          :allowed-depth="1"
          :dim-nested-items-weights="dimNestedItemsWeights"
          :element-types-for-unlink-action="[OBJECTIVE_TYPES.PERSONAL]"
          :item-type="LIST_ITEM_TYPES.PRIMARY_NEXT"
          :objectives="childKrs"
          :placement="LIST_ITEM_PLACEMENTS.OKR_ELEMENT_FORM"
          :some-item-is-expandable="someItemIsExpandable"
          always-show-weight
          fetch-child-objectives-on-expand
          show-status
          show-warning-icon
          type="default-next"
          @expand-item="expandItem"
          @update-elements="onChildUpdated"
          @okr-element-deleted="onChildUpdated"
          @link-jira-issue="$emit('link-jira-issue', okrElementToLinkIssuePayload($event))"
          @create-jira-issue="createJiraIssue($event)"
          @edit-element="$emit('edit-element', $event)"
          @link-work-item="$emit('link-work-item', okrElementToLinkWorkItemPayload($event))"
          @create-work-item="onCreateWorkItemForNestedElement"
          @unlink-element="unlinkChildElement"
        >
          <template v-if="!childKrs.length" #no-objectives>
            <AddNestedItem
              v-if="createNestedElementDropdownItems.length"
              :disabled="!userCanCreateObjectives"
              :dropdown-options="createNestedElementDropdownItems"
              data-auto-testid="add-nested-element"
              disable-margin
              follow-cursor="initial"
              @on-option-click="onActionMenuItemsClick"
            >
              {{
                $t(
                  userCanCreateObjectives
                    ? 'kr.add_nested_item'
                    : 'objective.btn.no_nested_objectives'
                )
              }}
            </AddNestedItem>
          </template>
        </OkrElementsList>
      </FormNestedItemsField>

      <OkrElementsFormCustomFieldsHub
        v-model:loading="contentCustomFieldsLoading"
        :data-testid="OKR_ELEMENT_FORM_CUSTOM_FIELD_HUB_TEST_ID"
        :element-id="modelValue.id || null"
        :fields-values="customFieldsValues"
        :is-edit="isEdit"
        :okr-type-id="CUSTOM_FIELDS_OKR_TYPE_IDS.KR.id"
        :user-has-update-access="userCanUpdateCustomFields"
        :workspace-id="workspaceId"
        @update-field-value="onUpdateCustomFieldValue"
        @update-activities="updateActivities"
      />

      <Activities
        v-if="isEdit"
        :ref="elementFormReferences.activities"
        :model-value="modelValue"
        :unit="unitForActivities"
        class="krf-Activities"
        @updated-progress-history="onProgressHistoryUpdated"
      />

      <template #sidebar>
        <div class="krf-Sidebar">
          <FormGradeAndStatus
            v-if="isEdit"
            :disabled="isFieldDisabled"
            :element-target-periods="
              milestonesSettings[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS] ||
              DEFAULT_MILESTONES_SETTINGS[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS]
            "
            :form-model="{
              ...formModel,
              currentValue,
              [OKR_ELEMENT_KEYS.PERMISSIONS]: modelValue[OKR_ELEMENT_KEYS.PERMISSIONS] || []
            }"
            :grade="modelValue.gradeToUse"
            :grade-color="modelValue.gradeColor"
            :is-okr-element-closed="isOkrElementClosed"
            :predicted-score="showPredictedScore ? modelValue.predictedGrade : 0"
            :show-predicted-score="showPredictedScore"
            data-testid="form-grade-and-status"
            @save-grade="onProgressValueUpdated"
            @open-checkin-modal="openCheckinModal"
          >
            <template #status-right>
              <ProgressDescription
                v-if="
                  showProgressDescription &&
                  Boolean(milestonesSettings[OKR_ELEMENT_KEYS.GRANULATION_ID])
                "
                :disabled="elementDataLoading"
                :element-id="modelValue.id"
                :grade="modelValue.gradeToUse"
                :grade-color="modelValue.gradeColor"
                :granulation-id="milestonesSettings[OKR_ELEMENT_KEYS.GRANULATION_ID]"
                :progress-aggregation-type-id="
                  milestonesSettings[OKR_ELEMENT_KEYS.PROGRESS_AGGREGATION_TYPE_ID]
                "
              />

              <StatusIndicator
                v-else-if="showPredictedScore"
                :grade="modelValue.gradeForConfidenceLevel"
                :grade-color="modelValue.gradeColor"
                :okr-element="formModel"
                :passed="modelValue.timePassedPercent"
                :predicted-score="modelValue.predictedGrade"
                data-auto-testid="status-indicator"
                data-testid="status-indicator"
              />
            </template>
          </FormGradeAndStatus>

          <!--
          <div
            v-if="showChart && modelValue.uniqueId"
            class="krf-Progress"
            data-auto-testid="progress-chart"
            data-testid="progress-chart"
          >
            <ObjectiveChart
              ref="objectiveChart"
              :element-id="modelValue.uniqueId"
              :grade-color="modelValue.gradeColor"
              :workspace-id="formModel.workspaceId"
              chart-type="simple-next"
              has-full-size-toggle
            />
          </div>
-->

          <FormItemsGroup :disabled="disableOnEdit" data-auto-testid="general-fields-group">
            <AppSelect
              v-model="formModel.levelId"
              :disabled="disableOnEdit"
              :loading="isLevelsLoading"
              :offset="[0, 0]"
              :options="metricLevels"
              :skeleton-loader-animation-opacity="selectSkeletonLoadersOpacity"
              data-auto-testid="metric-type-field"
              data-testid="metric-type-field"
              item-label="name"
              item-value="id"
              skeleton-loader
              @update:model-value="onParameterUpdate(OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.TYPE)"
            >
              <template #button="{ option, active }">
                <OkrFormFieldSelectTriggerNext :opened="active">
                  <OkrTypeFieldOption v-if="option" :option="option" />
                </OkrFormFieldSelectTriggerNext>
              </template>

              <template #option-label="{ option }">
                <OkrTypeFieldOption :option="option" />
              </template>
            </AppSelect>

            <AppSelect
              v-if="!isElementOwnerFieldHidden"
              v-model="formModel.ownerId"
              :disabled="disableOnEdit"
              :loading="assigneesAreLoading"
              :offset="[0, 0]"
              :options="ownerFieldOptions"
              :search-function="getAssignees"
              :skeleton-loader-animation-opacity="selectSkeletonLoadersOpacity"
              data-auto-testid="owner-field"
              data-testid="owner-field"
              dropdown-search
              item-label="name"
              item-value="accountId"
              skeleton-loader
              @change="onOwnerSelect"
              @update:options="assignees = $event"
              @update:model-value="onParameterUpdate(OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.OWNER)"
            >
              <template #button="{ option, active }">
                <OkrFormFieldSelectTriggerNext
                  :empty-state-label="$t('field.owner.placeholder')"
                  :opened="active"
                >
                  <OwnerFieldOption v-if="option" :option="option" />
                </OkrFormFieldSelectTriggerNext>
              </template>
              <template #option-label="{ option }">
                <OwnerFieldOption :option="option" />
              </template>
            </AppSelect>

            <AppSelect
              v-model="formModel.groups"
              :disabled="disableOnEdit"
              :loading="isTeamsLoading"
              :offset="[0, 0]"
              :options="groups"
              :search-function="getGroups"
              :skeleton-loader-animation-opacity="selectSkeletonLoadersOpacity"
              data-auto-testid="groups-field"
              data-testid="groups-field"
              has-only-this-button
              hide-selected-items-in-dropdown
              inline-search
              item-label="name"
              item-value="id"
              multi
              show-selected-options-inside
              skeleton-loader
              @change="onGroupSelect"
              @update:options="groups = $event"
              @update:model-value="onParameterUpdate(OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.GROUP)"
            >
              <template #option-label="{ option }">
                <GlobalGroupsSelectOption v-if="option" :group="option" />
              </template>
              <template #button="{ fullDataOption, active }">
                <OkrFormFieldSelectTriggerNext
                  :empty-state-label="$t('field.group.placeholder')"
                  :label="$t('field.groups.title')"
                  :opened="active"
                  :selected-options="fullDataOption"
                  style="--value-item-padding: 0"
                >
                  <template #value-item-content="{ item }">
                    <GroupSelectLabel v-if="item" :group="item" />
                  </template>
                </OkrFormFieldSelectTriggerNext>
              </template>
            </AppSelect>
          </FormItemsGroup>

          <FormItemsGroup :disabled="disableOnEdit" data-auto-testid="common-fields-group">
            <AppSelect
              :disabled="disableOnEdit"
              :hidden-items="archivedIntervals"
              :loading="isIntervalsLoading"
              :model-value="formModel.intervalId"
              :offset="[0, 0]"
              :options="getIntervalsAsOptions"
              :skeleton-loader-animation-opacity="selectSkeletonLoadersOpacity"
              data-auto-testid="interval-field"
              data-testid="interval-field"
              skeleton-loader
              @update:model-value="onUpdateInterval"
            >
              <template #button="{ option, active }">
                <OkrFormFieldSelectTriggerNext
                  :empty-state-label="$t('field.quoter.placeholder')"
                  :label="$t('field.quoter.title')"
                  :opened="active"
                  :selected-options="option?.label || ''"
                  separated-label
                />
              </template>
            </AppSelect>

            <OkrFormFieldSelectTriggerNext
              :disabled="isFieldDisabled"
              :opened="showDatesDropdown"
              data-auto-testid="dates-field-wrapper"
              data-testid="dates-field-wrapper"
              @click="onDatesTriggerClick"
            >
              <template #label>
                <div class="krf-DatesLabel">
                  <span class="krf-DatesLabel_Text">
                    {{ $t('objectives.table_header_dates') }}
                  </span>
                  <AppIcon
                    :icon-name="iconName"
                    class="krf-DatesLabel_Icon"
                    height="24"
                    width="24"
                  />
                  <span class="krf-DatesLabel_Value">
                    <OkrDatesDropdownTrigger
                      :active="showDatesDropdown"
                      :form-model="formModel"
                      :is-auto-period-mode="isAutoPeriodMode"
                      multi-line
                      type="default-next"
                    />

                    <OkrDatesDropdown
                      v-if="!isFieldDisabled && initialDataAreLoaded"
                      :follow-cursor="false"
                      :is-auto-period-mode="isAutoPeriodMode"
                      :objective="formModel"
                      :offset="[10, 10]"
                      :show-dates-dropdown="showDatesDropdown"
                      :show-period-mode-title="false"
                      append-to="body"
                      data-auto-testid="dates-field"
                      data-testid="dates-field"
                      position="bottom-end"
                      show-period-mode-switch
                      split-update-date-events
                      @hide-dates-dropdown="showDatesDropdown = false"
                      @update-element-date="onUpdateElementDate"
                      @update-period-mode="updatePeriodMode"
                    />
                  </span>
                </div>
              </template>
            </OkrFormFieldSelectTriggerNext>

            <AppSelect
              v-if="showFormField(formModel.labelIds.length > 0)"
              ref="labelsSelect"
              v-model="formModel.labelIds"
              :allow-create-on-search="allowCreateOnSearch"
              :create-label="$t('labels.new_label')"
              :disabled="disableOnEdit"
              :loading="isLabelsLoading"
              :offset="[0, 0]"
              :options="labels"
              :search-function="getLabels"
              :search-max-length="50"
              :skeleton-loader-animation-opacity="selectSkeletonLoadersOpacity"
              data-auto-testid="labels-field"
              data-testid="labels-field"
              has-only-this-button
              hide-selected-items-in-dropdown
              item-label="name"
              item-value="id"
              multi
              show-selected-options-inside
              skeleton-loader
              @create="createLabelOnSearch"
              @update:model-value="onParameterUpdate(OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.LABELS)"
            >
              <template #button="{ option, active }">
                <OkrFormFieldSelectTriggerNext
                  :empty-state-label="$t('field.labels.placeholder')"
                  :label="$t('field.labels.title')"
                  :opened="active"
                  :selected-options="option"
                />
              </template>
            </AppSelect>

            <AppSelect
              v-if="showFormField(formModel.stakeholders.length > 0)"
              v-model="formModel.stakeholders"
              :disabled="disableOnEdit"
              :dropdown-search="true"
              :hidden-items="[null]"
              :loading="assigneesAreLoading"
              :offset="[0, 0]"
              :options="assignees"
              :search-function="getAssignees"
              :skeleton-loader-animation-opacity="selectSkeletonLoadersOpacity"
              data-auto-testid="stakeholders-field"
              data-testid="stakeholders-field"
              has-only-this-button
              hide-selected-items-in-dropdown
              item-label="name"
              item-value="accountId"
              multi
              show-selected-options-inside
              skeleton-loader
              @update:options="assignees = $event"
              @update:model-value="
                onParameterUpdate(OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.STAKEHOLDERS)
              "
            >
              <template #button="{ fullDataOption, active }">
                <OkrFormFieldSelectTriggerNext
                  :empty-state-label="$t('filed.assignee.stakeholders.plug')"
                  :label="$t('field.assignee.stakeholders')"
                  :opened="active"
                  :selected-options="fullDataOption"
                  separated-label
                >
                  <template #values>
                    <SelectedUsersAvatarsList :users="fullDataOption" />
                  </template>
                </OkrFormFieldSelectTriggerNext>
              </template>

              <template #option-label="{ option }">
                <OwnerFieldOption :option="option" />
              </template>
            </AppSelect>
          </FormItemsGroup>

          <OkrElementsFormCustomFieldsHub
            :ref="SIDEBAR_CUSTOM_FIELDS_REFERENCE_KEY"
            v-model:loading="sidebarCustomFieldsLoading"
            :data-testid="OKR_ELEMENT_FORM_CUSTOM_FIELD_HUB_TEST_ID"
            :element-id="modelValue?.id || null"
            :fields-values="customFieldsValues"
            :is-edit="isEdit"
            :okr-type-id="CUSTOM_FIELDS_OKR_TYPE_IDS.KR.id"
            :placement="CUSTOM_FIELDS_PLACEMENTS.SIDEBAR"
            :user-has-update-access="userCanUpdateCustomFields"
            :workspace-id="workspaceId"
            @update-field-value="onUpdateCustomFieldValue"
            @update-activities="updateActivities"
          />
        </div>
      </template>

      <template #footer>
        <ModalFooterActions
          v-if="!isEdit"
          v-model="createAnother"
          :disabled="loading"
          :loading="loading"
          data-auto-testid="create-okr-footer"
          data-testid="create-okr-footer"
          @close="$emit('close')"
          @create="handleSaveKr"
        >
          <template #checkbox-label>
            {{ $t('create.okr_element.create_another') }}
          </template>

          <template #cancel-button-text>
            {{ $t('action.cancel') }}
          </template>

          <template #confirm-button-text> {{ $t('action.create') }}</template>
        </ModalFooterActions>
      </template>
    </SidebarLayout>

    <CreateJiraIssueForm
      :ref="elementFormReferences.createJiraIssueForm"
      source="form"
      @save="onCreateJiraIssueFormSave"
    />

    <CreateWorkItemForm
      :ref="elementFormReferences.createWorkItemForm"
      source="form"
      @save="onCreateWorkItemFormSave"
    />

    <CheckinModal
      v-if="isShowCheckinModal"
      v-model="isCheckinModalOpen"
      :entry-point="checkinModalLaunchData.entryPoint"
      :okr-element="checkinModalLaunchData.okrElement"
      :override-current-value="checkinModalLaunchData.overrideCurrentValue"
      :preset-backdate="checkinModalLaunchData.presetBackdate"
      :tracking-source="EVENT_SOURCES.FORM"
      @saved="onCheckinSaved"
      @after-leave="onCheckinModalClosed"
    />
  </div>

  <AppDialog
    :data-auto-testid="CONFIRM_INTERVAL_CHANGE_MODAL_TEST_ID"
    :data-testid="CONFIRM_INTERVAL_CHANGE_MODAL_TEST_ID"
    :show="isConfirmIntervalChangeShow"
    :title="$t('confirm_interval_change.title')"
    :type="DIALOG_TYPES.WARNING"
    @on-close="hideConfirmIntervalChange"
    @on-confirm="onConfirmIntervalChange"
  >
    {{ confirmDescription }}
  </AppDialog>
</template>

<script>
import { cloneDeep, has, isEmpty, isNull, isUndefined, uniqBy } from 'lodash'
import { defineComponent, nextTick, ref, toRefs } from 'vue'
import { mapGetters, mapState } from 'vuex'

import AssigneesInfoApiHandler from '@/api/assignees-info'
import GlobalGroupsApiHandler from '@/api/global-groups'
import IntervalsInfoApiHandler from '@/api/intervals-info'
import LabelsApiHandler from '@/api/labels'
import LevelApiHandler from '@/api/level'
import ObjectivesApiHandler from '@/api/okr-elements'
import ObjectivesInfoApiHandler, { SEARCH_TYPES } from '@/api/okr-elements'
import { useFlagReactive } from '@/composables/flag-reactive'
import { useScrollToComments } from '@/composables/scroll-to-comments'
import { tracker } from '@/tracking/amplitude'
import {
  EVENT_CATEGORIES,
  EVENT_SOURCES,
  trackLabelCreatedEvent,
  trackManualUpdateGradeEvent
} from '@/tracking/amplitude-helpers'
import { EVENT_NAMES } from '@/tracking/gtm-helpers'
import { gtmTracker } from '@/tracking/gtm-tracking'
import { getTabSlotName } from '@/utils/app-tabs'
import { useCreateWorkItem } from '@/utils/azure-devops-helpers/work-item'
import { useCheckinModal } from '@/utils/check-ins/check-ins'
import { BUTTON_SIZES } from '@/utils/components-configurations/app-button'
import { DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { LOCK_MESSAGE_TYPES } from '@/utils/components-configurations/lock-message'
import {
  LIST_ITEM_PLACEMENTS,
  LIST_ITEM_TYPES
} from '@/utils/components-configurations/okr-elements-list-item'
import { TYPES as RESULT_TYPE_SETTINGS_TYPES } from '@/utils/components-configurations/result-type-settings'
import { CUSTOM_FIELDS_PLACEMENTS } from '@/utils/custom-fields/factory'
import {
  createInitialCustomFieldsValuesForOkrElement,
  CUSTOM_FIELDS_OKR_TYPE_IDS,
  DEFAULT_CUSTOM_FIELDS_VALUES_FOR_OKR_ELEMENT,
  SIDEBAR_CUSTOM_FIELDS_REFERENCE_KEY,
  useOkrElementsFormCustomFieldsHub
} from '@/utils/custom-fields/helpers'
import { OKR_ELEMENT_FORM_CUSTOM_FIELD_HUB_TEST_ID } from '@/utils/custom-fields/jest-helpers'
import {
  ADD_NESTED_ELEMENT_EMIT_EVENTS,
  ELEMENT_PROGRESS_ADJUSTMENT_TABS,
  useCreateNestedElementDropdownItems,
  useElementForm,
  useElementProgressAdjustment,
  useParentElement,
  useProgressOptions
} from '@/utils/element-types'
import {
  ASSIGNEE_ENTITY_KEYS,
  COMPANY_SETTINGS_ENTITY_KEYS,
  OKR_ELEMENT_ENTITY_KEYS,
  REQUEST_ENTITY_KEYS
} from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { checkSomeValueIsTruthy, isStringEmpty } from '@/utils/general'
import {
  isCrossPlatformAppInjectionKey,
  isJiraAppInjectionKey,
  isWebAppInjectionKey
} from '@/utils/injection-keys'
import { getArchivedIntervals } from '@/utils/interval'
import { KR_VALUE_TYPES } from '@/utils/metrics-helpers'
import { CHART_TYPES, isRefreshChartDataNeed } from '@/utils/objective-chart'
import { isKR, OBJECTIVE_TYPES, TYPE_ID_NAMES } from '@/utils/objective-types'
import {
  convertGroupsListToGroups,
  convertUsersListToOwnerAndStakeholders,
  createGroupsList,
  currentUserCanUpdateObjective,
  getDescriptionForIntervalChangeConfirmation,
  getShowChartStatus,
  getNotSetOwnerOption,
  OBJECTIVE_SORT_OPTIONS,
  OKR_ELEMENT_FORM_MENU_ACTIONS,
  OKR_ELEMENT_PARAMETERS_SAVING_STATUSES,
  OKR_FORM_VIEWS,
  OKR_STATUSES,
  okrElementToLinkIssuePayload,
  currentUserCanReadObjective,
  isOkrElementClosed,
  currentUserCanCreateObjective,
  saveUpdatedChildParameters,
  getClosedStatusName,
  getMetricLevelsForAdd,
  okrElementToCreateIssuePayload,
  isBacklogIntervalSelected,
  isKPI,
  getSafeUpdatedElementParameters,
  okrElementToCreateWorkItemPayload,
  okrElementToLinkWorkItemPayload
} from '@/utils/objectives'
import {
  isManualPeriodModeEnabled,
  OKR_DATES_SELECT_DATE_PROPS,
  PERIOD_MODES
} from '@/utils/okr-element-dates'
import {
  DEFAULT_BINARY_ORIGINAL_VALUE,
  DEFAULT_BINARY_TARGET_VALUE,
  DEFAULT_MILESTONES_SETTINGS,
  DEFAULT_ORIGINAL_VALUE,
  DEFAULT_RESULT_TYPE_VALUES,
  DEFAULT_TARGET_VALUE,
  findPeriodGranulationOptionsByTypeId,
  findProgressAggregationOptionsByTypeId,
  getConditionParameterById,
  getCurrentValue,
  getOriginalValue,
  getProgressOptionByFieldTypeId,
  getResultTypeSettingsDisabledStates,
  getResultTypeValues,
  getTargetValue,
  isBinaryProgress,
  isBinarySelectDisabled,
  isStartTargetProgress,
  isThresholdProgress,
  METRIC_VALUES_KEYS,
  notEqualStringifiedValues
} from '@/utils/okr-element-values'
import { calculateGradeValue, getGradeDisplayValue } from '@/utils/okr-elements/grade'
import { MATH_SYMBOLS } from '@/utils/okr-elements/math'
import {
  checkIsExpandableNestedItemsExist,
  createGetGroupsPayload,
  createGetUsersPayload,
  createPayloadForExpandAllNestedItems,
  getLockMessagesStates,
  isFormModelsEqual,
  isMilestonesSettingsChanged,
  useElementOwner
} from '@/utils/okr-elements-forms-helpers'
import { isAllNestedItemsWeightsDimmed } from '@/utils/okr-weights-helpers'
import { getSimplifiedNumber, PLURALIZATION_KEYS } from '@/utils/pluralization'
import { DESCRIPTION_FILED_LIMITS } from '@/utils/rich-edit'
import { uid } from '@/utils/uid'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'
import { usePlansLimitations } from '@/utils/web-app/plans-limitations'

import AppDialog from '@/components/AppDialog'
import OkrElementsFormCustomFieldsHub from '@/components/custom-fields/okr-elements-form/OkrElementsFormCustomFieldsHub'
import ElementDataSource from '@/components/form/ElementDataSource'
import ElementProgressChart from '@/components/form/ElementProgressChart'
import ElementProgressMilestones from '@/components/form/ElementProgressMilestones'
import FormGradeAndStatus from '@/components/form/FormGradeAndStatus'
import FormItemsGroup from '@/components/form/FormItemsGroup'
import FormNestedItemsField from '@/components/form/FormNestedItemsField'
import {
  ELEMENT_PROGRESS_SETUP_BACK_BUTTON_TEST_ID,
  ELEMENT_PROGRESS_SETUP_BUTTON_TEST_ID
} from '@/components/form/jest-helpers'
import ResultTypeSettings from '@/components/form/key-result/ResultTypeSettings'
import ResultTypeSettingsDefaultRightSide from '@/components/form/key-result/ResultTypeSettingsDefaultRightSide'
import LockMessage from '@/components/form/LockMessage'
import OkrTypeFieldOption from '@/components/form/OkrTypeFieldOption'
import OwnerFieldOption from '@/components/form/OwnerFieldOption'
import SelectedUsersAvatarsList from '@/components/form/SelectedUsersAvatarsList'
import GlobalGroupsSelectOption from '@/components/global-groups/GlobalGroupsSelectOption'
import GroupSelectLabel from '@/components/global-groups/GroupSelectLabel'
import CheckinModal from '@/components/objectives/element-check-in/CheckinModal'
import AddNestedItem from '@/components/objectives/forms/AddNestedItem'
import DescriptionField from '@/components/objectives/forms/DescriptionField'
import { CONFIRM_INTERVAL_CHANGE_MODAL_TEST_ID } from '@/components/objectives/forms/jest-helpers'
import MetricUnitInput from '@/components/objectives/forms/MetricUnitInput'
import ModalFooterActions from '@/components/objectives/forms/ModalFooterActions'
import Activities from '@/components/objectives/forms/objective-form/Activities'
import OkrTitleFiled from '@/components/objectives/forms/OkrTitleFiled'
import ParentElementSelect from '@/components/objectives/forms/ParentElementSelect'
import WeightsModalTrigger from '@/components/objectives/forms/WeightsModalTrigger'
import OkrElementsList from '@/components/objectives/okr-elements-list/OkrElementsList'
import OkrDatesDropdown from '@/components/objectives/OkrDatesDropdown'
import OkrDatesDropdownTrigger from '@/components/objectives/OkrDatesDropdownTrigger'
import ProgressDescription from '@/components/objectives/progress-description/ProgressDescription'
import StatusIndicator from '@/components/objectives/status-indicator/StatusIndicator'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'
import AppRadioGroupNextOptionWithIcon from '@/components/ui/AppRadioGroup/AppRadioGroupNextOptionWithIcon'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import OkrFormFieldSelectTriggerNext from '@/components/ui/AppSelect/TriggerButtons/OkrFormFieldSelectTriggerNext'
import AppTabs from '@/components/ui/AppTabs/AppTabs'
import SidebarLayout from '@/components/ui/SidebarLayout/SidebarLayout'
import CreateJiraIssueForm from '@jira/components/objectives/forms/CreateJiraIssue'
import CreateWorkItemForm from '@jira/components/objectives/forms/CreateWorkItemForm'

/*
 * grade:
 *   for grade are responsible three values in form data: originalValue, targetValue and
 *   currentValue.
 *   - originalValue is set on KR creating and can't be changed anymore(and should not be sent in
 *     request),
 *   - if grade type is binary and on creating originalValue was set to 1, then currentValue is
 *     also 1 and cannot be returned to 0(originalValue === targetValue)
 */

const DEFAULT_FORM_MODEL = {
  name: '',
  ...DEFAULT_RESULT_TYPE_VALUES,
  description: '',
  parentId: null,
  labelIds: [],
  groups: [],
  ownerId: null,
  stakeholders: [],
  workspaceId: null,
  dueDate: null,
  elementStartDate: null,
  startDateManual: PERIOD_MODES.MANUAL.value,
  dueDateManual: PERIOD_MODES.MANUAL.value,
  automaticElementStartDate: null,
  automaticDueDate: null,
  confidenceLevelId: OKR_STATUSES.AUTO,
  automaticConfidenceLevelId: 0,
  intervalId: null,
  levelId: 0,
  contribute: true,
  private: false,
  updateStatusForNestedElements: false
}

export default defineComponent({
  name: 'KeyResult',

  components: {
    CreateWorkItemForm,
    AppButton,
    CheckinModal,
    ProgressDescription,
    AppTabs,
    ElementProgressMilestones,
    ElementProgressChart,
    MetricUnitInput,
    ParentElementSelect,
    OkrTypeFieldOption,
    OkrElementsFormCustomFieldsHub,
    GlobalGroupsSelectOption,
    GroupSelectLabel,
    ResultTypeSettingsDefaultRightSide,
    AppDialog,
    WeightsModalTrigger,
    LockMessage,
    AddNestedItem,
    FormNestedItemsField,
    ModalFooterActions,
    OkrTitleFiled,
    AppRadioGroupNextOptionWithIcon,
    SelectedUsersAvatarsList,
    ResultTypeSettings,
    FormGradeAndStatus,
    OwnerFieldOption,
    FormItemsGroup,
    OkrFormFieldSelectTriggerNext,
    AppRadioGroup,
    OkrDatesDropdown,
    OkrDatesDropdownTrigger,
    OkrElementsList,
    AppIcon,
    AppSelect,
    DescriptionField,
    Activities,
    SidebarLayout,
    CreateJiraIssueForm,
    StatusIndicator,
    ElementDataSource
  },

  inject: {
    isWebApp: {
      from: isWebAppInjectionKey
    },

    isCrossPlatformApp: {
      from: isCrossPlatformAppInjectionKey
    },

    isJiraApp: {
      from: isJiraAppInjectionKey
    }
  },

  props: {
    // required parameters:
    // create: intervalId, levelId, workspaceId
    modelValue: {
      type: Object,
      default: null
    },

    source: {
      type: String,
      default: ''
    },

    navigationTab: {
      type: String,
      default: ''
    },

    childrenOrder: {
      type: Array,
      default: () => [OBJECTIVE_SORT_OPTIONS.ORDER_ASC]
    },

    allowCreateElements: {
      type: Boolean
    },

    okrPayload: {
      type: Object,
      default: () => ({})
    },

    elementDataLoading: {
      type: Boolean
    },

    childModalOpened: {
      type: Boolean
    }
  },

  CHART_TYPES: { ...CHART_TYPES },

  OKR_DATES_SELECT_DATE_PROPS: { ...OKR_DATES_SELECT_DATE_PROPS },

  emits: {
    'open-custom-weights': null,
    'link-jira-issue': null,
    'link-work-item': null,
    'edit-element': null,
    close: null,
    'saving-started': null,
    'saving-ended': null,
    'on-created': null,
    'update:model-value': null,
    update: null,
    'on-parent-objective-id-click': null,
    [ADD_NESTED_ELEMENT_EMIT_EVENTS.OPEN_CHILD_OBJECTIVE]: null,
    [ADD_NESTED_ELEMENT_EMIT_EVENTS.OPEN_KR]: null,
    [ADD_NESTED_ELEMENT_EMIT_EVENTS.CREATE_NESTED_WHILE_CREATE_CURRENT]: null,
    'level-changed': null,
    'set-child-modal-opened': null
  },

  setup(props) {
    const { okrWeightsLimitations, dataSourceLimitations } = usePlansLimitations()
    const formModel = ref(cloneDeep(DEFAULT_FORM_MODEL))

    const { modelValue } = toRefs(props)

    const {
      workspaceId,
      createJiraIssue,
      startWorkItemCreation,
      updateActivities,
      setFocusOnTitle,
      isEdit,
      editable,
      initialDataAreLoaded,
      isOkrElementClosed,
      disableOnEdit,
      showWeightsModalTrigger,
      selectSkeletonLoadersOpacity,
      portalName,
      elementFormReferences,
      isShowProgressLockMessage,
      milestonesSettings,
      requestDataUpdateFromMilestones,
      restoreToDefaultsMilestones,
      forceUpdateMilestones,
      saveMilestonesSettingsFromFormModel,
      resultTypeSettingsFieldsCount,
      isResultTypeSettingsDisabled
    } = useElementForm({
      modelValue,
      formView: OKR_FORM_VIEWS.KR,
      formModel
    })

    const { getProgressOptions: RESULT_TYPE_OPTIONS } = useProgressOptions({
      formModel,
      isEdit
    })

    const { isShowParentRequiredError, isShowUnlinkParentButton, validateParentElement } =
      useParentElement({
        formModel,
        isEdit
      })

    const { createNestedElementDropdownItems, getPayloadForHandleAction } =
      useCreateNestedElementDropdownItems({
        modelValue
      })

    const { currentProgressAdjustmentTab, progressAdjustmentTabs } = useElementProgressAdjustment({
      isEdit,
      isResultTypeSettingsDisabled
    })

    const {
      isCheckinModalOpen,
      handleOpenCheckinModal,
      checkinModalLaunchData,
      isShowCheckinModal,
      onCheckinModalClosed
    } = useCheckinModal()

    const { isElementOwnerFieldHidden, createUsersListForPayload } = useElementOwner({
      [OKR_ELEMENT_ENTITY_KEYS.TYPE_ID]: OBJECTIVE_TYPES.KR
    })

    const { reloadUsersListsInCustomFieldsHub } = useOkrElementsFormCustomFieldsHub()

    const { isAnyFlagEnabled, runWithFlag } = useFlagReactive({
      object: OKR_ELEMENT_PARAMETERS_SAVING_STATUSES
    })

    const setFocusOnTitleWrapper = () => {
      if (!props.childModalOpened) {
        setFocusOnTitle()
      }
    }

    const { setup: setupScrollToComments } = useScrollToComments()

    const { firstWorkItemKindId } = useCreateWorkItem()

    return {
      okrWeightsLimitations,
      RESULT_TYPE_OPTIONS,
      formModel,
      isEdit,
      isShowParentRequiredError,
      validateParentElement,
      isShowUnlinkParentButton,
      createNestedElementDropdownItems,
      getPayloadForHandleAction,
      workspaceId,
      createJiraIssue,
      startWorkItemCreation,
      updateActivities,
      setFocusOnTitle: setFocusOnTitleWrapper,
      initialDataAreLoaded,
      editable,
      isOkrElementClosed,
      disableOnEdit,
      selectSkeletonLoadersOpacity,
      portalName,
      showWeightsModalTrigger,
      elementFormReferences,
      currentProgressAdjustmentTab,
      progressAdjustmentTabs,
      isShowProgressLockMessage,
      milestonesSettings,
      requestDataUpdateFromMilestones,
      restoreToDefaultsMilestones,
      forceUpdateMilestones,
      saveMilestonesSettingsFromFormModel,
      resultTypeSettingsFieldsCount,
      isCheckinModalOpen,
      handleOpenCheckinModal,
      checkinModalLaunchData,
      isShowCheckinModal,
      onCheckinModalClosed,
      isElementOwnerFieldHidden,
      createUsersListForPayload,
      reloadUsersListsInCustomFieldsHub,

      isAnyOkrElementParametersSavingStatusesEnabled: isAnyFlagEnabled,
      runWithFlagOkrParamSavingStatus: runWithFlag,
      setupScrollToComments,

      dataSourceLimitations,
      firstWorkItemKindId
    }
  },

  data() {
    return {
      uid: uid(),
      localFormModel: cloneDeep(DEFAULT_FORM_MODEL),
      isTitleEmpty: false,
      loading: false,
      isIntervalsLoading: false,
      isLabelsLoading: false,
      areIntervalsLoaded: false,
      assigneesAreLoading: false,
      isTeamsLoading: false,
      isParentObjectivesLoading: false,
      areAssigneesLoaded: false,
      isLevelsLoading: false,
      areGroupsLoaded: false,
      areParentObjectivesLoaded: false,

      isGroupManuallyChanged: false,
      isOwnerManuallyChanged: false,
      assignees: [],
      parentObjectives: [],
      groups: [],
      levels: [],
      childKrs: [],
      intervals: [],
      labels: [],
      createAnother: false,

      binaryValueIsSaving: false,
      originalValueIsSaving: false,
      currentValueIsSaving: false,
      targetValueIsSaving: false,
      thresholdConditionIsSaving: false,

      showChart: false,

      isConfirmIntervalChangeShow: false,
      temporaryIntervalId: null,

      showDatesDropdown: false,

      weightIsManual: false,

      isExpandAllLoading: false,
      resultTypeSettingsUid: uid(),

      contentCustomFieldsLoading: false,
      sidebarCustomFieldsLoading: false,
      customFieldsValues: DEFAULT_CUSTOM_FIELDS_VALUES_FOR_OKR_ELEMENT,

      descriptionWasChanged: false
    }
  },

  computed: {
    ...mapGetters('system', {
      okrElementModalFullscreenState: 'okrElementModalFullscreenState'
    }),

    ...mapState('pluginOptions', {
      isPluginAdmin: state => state.isPluginAdmin
    }),

    ...mapState('system', {
      settings: state => state.settings,
      userData: state => state.userData || {}
    }),

    ...mapGetters('customFields', {
      fieldsByWorkspaceId: 'fieldsByWorkspaceId'
    }),

    ...mapGetters('workspaces', {
      selectedWorkspace: 'selectedWorkspace'
    }),

    // do not remove, please. it is used in the template
    chartTabName() {
      return getTabSlotName(ELEMENT_PROGRESS_ADJUSTMENT_TABS.CHART.value)
    },

    // do not remove, please. it is used in the template
    setupTabName() {
      return getTabSlotName(ELEMENT_PROGRESS_ADJUSTMENT_TABS.SETUP.value)
    },

    SIDEBAR_CUSTOM_FIELDS_REFERENCE_KEY: () => SIDEBAR_CUSTOM_FIELDS_REFERENCE_KEY,

    EVENT_SOURCES: () => EVENT_SOURCES,

    LIST_ITEM_TYPES: () => LIST_ITEM_TYPES,

    BUTTON_SIZES: () => BUTTON_SIZES,

    DESCRIPTION_FILED_LIMITS: () => DESCRIPTION_FILED_LIMITS,

    OBJECTIVE_TYPES: () => OBJECTIVE_TYPES,

    DEFAULT_MILESTONES_SETTINGS: () => DEFAULT_MILESTONES_SETTINGS,

    OKR_ELEMENT_KEYS: () => OKR_ELEMENT_KEYS,

    ELEMENT_PROGRESS_ADJUSTMENT_TABS: () => ELEMENT_PROGRESS_ADJUSTMENT_TABS,

    DROP_LIST_THEMES: () => DROP_LIST_THEMES,

    LIST_ITEM_PLACEMENTS: () => LIST_ITEM_PLACEMENTS,

    LOCK_MESSAGE_TYPES: () => LOCK_MESSAGE_TYPES,

    OKR_ELEMENT_FORM_CUSTOM_FIELD_HUB_TEST_ID: () => OKR_ELEMENT_FORM_CUSTOM_FIELD_HUB_TEST_ID,

    CUSTOM_FIELDS_OKR_TYPE_IDS: () => CUSTOM_FIELDS_OKR_TYPE_IDS,

    CUSTOM_FIELDS_PLACEMENTS: () => CUSTOM_FIELDS_PLACEMENTS,

    DIALOG_TYPES: () => DIALOG_TYPES,

    CONFIRM_INTERVAL_CHANGE_MODAL_TEST_ID: () => CONFIRM_INTERVAL_CHANGE_MODAL_TEST_ID,

    ELEMENT_PROGRESS_SETUP_BUTTON_TEST_ID: () => ELEMENT_PROGRESS_SETUP_BUTTON_TEST_ID,

    ELEMENT_PROGRESS_SETUP_BACK_BUTTON_TEST_ID: () => ELEMENT_PROGRESS_SETUP_BACK_BUTTON_TEST_ID,

    OKR_ELEMENT_PARAMETERS_SAVING_STATUSES: () => OKR_ELEMENT_PARAMETERS_SAVING_STATUSES,

    unitForActivities() {
      if (this.isBinary) return ''

      if (this.isLockedOnPercent) return MATH_SYMBOLS.PERCENT

      return this.formModel.unit || ''
    },

    isShowResultTypeSettings() {
      return Boolean(this.formModel.fieldTypeId) && (!this.isEdit || this.initialDataAreLoaded)
    },

    isShowChartTable() {
      return isKPI(this.modelValue) && isKPI(this.formModel) && !this.isLockedOnPercent
    },

    updateScrollbarTriggers() {
      // const elementTargetPeriods =
      //   this.milestonesSettings?.[OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS] || []
      //
      // const indexes = Array.from({ length: elementTargetPeriods.length }, (_, i) => `${i}`)

      return [this.modelValue?.id].filter(Boolean)
    },

    getResultTypeSettingsType() {
      const { CENTERED, DEFAULT } = RESULT_TYPE_SETTINGS_TYPES
      return this.okrElementModalFullscreenState ? CENTERED : DEFAULT
    },

    currentWorkspaceCustomFields() {
      return this.fieldsByWorkspaceId(this.workspaceId)
    },

    selectedParentElement() {
      return this.parentObjectives.find(objective => objective.id === this.formModel.parentId)
    },

    getOkrElementClosedStatusName() {
      const statusName = getClosedStatusName({
        okrElement: this.modelValue
      })
      return this.$t(statusName).toLowerCase()
    },

    getParentElementClosedStatusName() {
      const statusName = getClosedStatusName({
        okrElement: this.selectedParentElement
      })
      return this.$t(statusName).toLowerCase()
    },

    parentElementReadable() {
      if (!this.selectedParentElement) return false

      return currentUserCanReadObjective(this.selectedParentElement)
    },

    ownerFieldOptions() {
      return uniqBy(
        [getNotSetOwnerOption(this.$t), ...this.assignees],
        ASSIGNEE_ENTITY_KEYS.ACCOUNT_ID
      )
    },

    dimNestedItemsWeights() {
      return isAllNestedItemsWeightsDimmed({
        weightIsManual: this.weightIsManual,
        nestedItems: this.childKrs
      })
    },

    someItemIsExpandable() {
      return checkIsExpandableNestedItemsExist(this.childKrs)
    },

    resultTypeSettingsDisabledStates() {
      const { select, isSwitch, input } = getResultTypeSettingsDisabledStates({
        isEdit: this.isEdit,
        editable: this.editable,
        isLockedOnPercent: this.isLockedOnPercent,
        isBinarySelectDisabled: this.isBinarySelectDisabled,
        valuesAreReadonly: this.valuesAreReadonly
      })
      return {
        select,
        switch: isSwitch,
        input
      }
    },

    existContributeChildren() {
      return this.modelValue && this.modelValue.existContributeChildren
    },

    showProgressDescription() {
      return this.isEdit && !this.isLockedOnPercent && isKPI(this.formModel)
    },

    showPredictedScore() {
      if (this.isBinary) return false

      const baseCondition =
        this.formModel.confidenceLevelId === OKR_STATUSES.AUTO &&
        this.isEdit &&
        this.showChart &&
        this.modelValue.timePassedPercent >= 0

      if (this.isThreshold) {
        return baseCondition && this.isLockedOnPercent
      }

      return baseCondition
    },

    isInBacklogInterval() {
      return isBacklogIntervalSelected({
        selectedIntervalId: this.modelValue.intervalId,
        intervals: this.intervals
      })
    },

    chartTriggers() {
      const { intervals, modelValue } = this
      const { intervalId } = modelValue
      return {
        intervals,
        intervalId
      }
    },

    isAutoPeriodMode() {
      return !isManualPeriodModeEnabled(
        this.formModel.startDateManual,
        this.formModel.dueDateManual
      )
    },

    automaticElementDatesForCreate() {
      const { intervalId } = this.formModel
      const currentInterval = this.intervals.find(item => item.id === intervalId)
      return {
        startDate: currentInterval?.startDate,
        dueDate: currentInterval?.endDate
      }
    },

    iconName() {
      return this.isAutoPeriodMode ? PERIOD_MODES.AUTO.icon : PERIOD_MODES.MANUAL.icon
    },

    isFieldDisabled() {
      return this.isEdit && !this.userCanUpdateObjective
    },

    confirmDescription() {
      return getDescriptionForIntervalChangeConfirmation({
        formModel: this.formModel,
        i18nInstance: this.$t
      })
    },

    isLockedOnPercent() {
      return this.modelValue?.existContributeChildren
    },

    originalValue: {
      get() {
        return getOriginalValue(this.formModel)
      },

      set(value) {
        if (this.isNumber) {
          this.formModel[METRIC_VALUES_KEYS.NUMBER_ORIGINAL_VALUE] = value
        } else if (this.isThreshold) {
          this.formModel[METRIC_VALUES_KEYS.THRESHOLD_ORIGINAL_VALUE] = value
        }
      }
    },

    targetValue: {
      get() {
        return getTargetValue(this.formModel)
      },

      set(value) {
        if (this.isNumber) {
          this.formModel[METRIC_VALUES_KEYS.NUMBER_TARGET_VALUE] = value
        } else if (this.isThreshold) {
          this.formModel[METRIC_VALUES_KEYS.THRESHOLD_TARGET_VALUE] = value
        }
      }
    },

    currentValue: {
      get() {
        // need for display current grade in auto grade calculation mode in current value input
        if (this.isEdit) {
          return this.existContributeChildren
            ? getSimplifiedNumber(getGradeDisplayValue(this.grade))[PLURALIZATION_KEYS.FLOAT_NUMBER]
            : getCurrentValue(this.formModel)
        }
        return getCurrentValue(this.formModel)
      },

      set(value) {
        if (this.isNumber) {
          this.formModel[METRIC_VALUES_KEYS.NUMBER_CURRENT_VALUE] = value
        } else if (this.isThreshold) {
          this.formModel[METRIC_VALUES_KEYS.THRESHOLD_CURRENT_VALUE] = value
        }
        // else {
        //   this.formModel.currentValue = value;
        // }
      }
    },

    thresholdCondition: {
      get() {
        return this.formModel.thresholdConditionTypeId
      },

      set(value) {
        this.formModel.thresholdConditionTypeId = value
      }
    },

    resultTypeSettingsFormModel() {
      // need for display correct binary value in auto grade calculation  mode with binary option
      if (this.isEdit && this.existContributeChildren && this.isBinary) {
        return {
          ...this.formModel,
          [METRIC_VALUES_KEYS.BINARY_VALUE]: Number(
            getSimplifiedNumber(getGradeDisplayValue(this.grade))[
              PLURALIZATION_KEYS.DEFAULT_NUMBER
            ] >= 100
          )
        }
      }

      return this.formModel
    },

    getObjectivesAsOptions() {
      return this.parentObjectives.filter(o => {
        if (this.modelValue) {
          return o.id !== this.modelValue.id
        }
        return true
      })
    },

    getCurrentParentElement() {
      return this.getObjectivesAsOptions.find(objective => objective.id === this.formModel.parentId)
    },

    isBinary() {
      return isBinaryProgress({ element: this.formModel })
    },

    isThreshold() {
      return isThresholdProgress({ element: this.formModel })
    },

    isNumber() {
      return isStartTargetProgress({ element: this.formModel })
    },

    isBinarySelectDisabled() {
      return isBinarySelectDisabled(this.modelValue)
    },

    skipDescriptionCheck() {
      // ↓ optional channing coz in case of closed okr elements we hide description field
      if (this.$refs.description?.active) {
        return false
      }
      return !this.descriptionWasChanged
    },

    milestonesDataChanged() {
      if (this.isEdit) {
        return isMilestonesSettingsChanged({
          modelValue: this.modelValue,
          milestonesSettings: this.milestonesSettings
        })
      }

      return false
    },

    isUnitChanged() {
      if (this.isEdit) {
        return this.modelValue.unit !== this.formModel.unit
      }

      return false
    },

    areDataChanged() {
      let resultTypesValuesChanged = false

      if (this.isEdit) {
        resultTypesValuesChanged =
          Number(this.originalValue) !== this.modelValue.originalValue ||
          Number(getCurrentValue(this.formModel)) !== Number(this.modelValue.currentValue) ||
          Number(this.targetValue) !== this.modelValue.targetValue
      } else {
        resultTypesValuesChanged =
          Number(this.originalValue) !== DEFAULT_ORIGINAL_VALUE ||
          Number(this.targetValue) !== DEFAULT_TARGET_VALUE
      }

      return (
        !isFormModelsEqual({
          localFormModel: this.localFormModel,
          formModel: this.formModel,
          skipDescriptionCheck: this.skipDescriptionCheck
        }) ||
        resultTypesValuesChanged ||
        this.milestonesDataChanged
      )
    },

    isDescriptionTooLong() {
      // in some cases(for example when linked jira issue is being edited)
      // description in formModel(store) can be undefined
      if (!this.formModel.description) {
        return false
      }
      return this.formModel.description.length > 18000
    },

    valuesAreReadonly() {
      return this.isLockedOnPercent || this.existContributeChildren
    },

    metricLevels() {
      return getMetricLevelsForAdd(this.levels)
    },

    editDataAreSaving() {
      return (
        this.isEdit &&
        checkSomeValueIsTruthy(
          this.binaryValueIsSaving,
          this.originalValueIsSaving,
          this.currentValueIsSaving,
          this.targetValueIsSaving,
          this.thresholdConditionIsSaving,
          this.isAnyOkrElementParametersSavingStatusesEnabled,
          this.contentCustomFieldsLoading,
          this.sidebarCustomFieldsLoading
        )
      )
    },

    grade() {
      if (this.isEdit) {
        return this.modelValue.gradeToUse
      }

      return calculateGradeValue(this.targetValue, this.originalValue, this.currentValue)
    },

    getIntervalsAsOptions() {
      return this.intervals.map(i => ({
        label: i.name,
        value: i.id
      }))
    },

    // passing archived intervals as hidden-items because
    // OKR element can has archived interval as selected
    // so we must show it like selected but dont pass it to all options list
    archivedIntervals() {
      return getArchivedIntervals(this.intervals).map(item => item.id)
    },

    userCanUpdateObjective() {
      return this.modelValue && currentUserCanUpdateObjective(this.modelValue)
    },

    userCanUpdateCustomFields() {
      if (this.isEdit) {
        return this.userCanUpdateObjective && !this.isOkrElementClosed
      }

      return true
    },

    // userCanCreateObjectives() {
    //   return currentUserCanCreateObjective(this.selectedWorkspace)
    // },
    getParentOkrTypeName() {
      return isKR(this.selectedParentElement)
        ? this.$t('okr_elements.kr')
        : this.$t('okr_elements.objective')
    },

    isClosedParent() {
      if (!this.formModel.parentId) return false
      return this.selectedParentElement && isOkrElementClosed(this.selectedParentElement)
    },

    userCanCreateObjectives() {
      return currentUserCanCreateObjective(this.selectedWorkspace, this.isPluginAdmin)
    },

    allowCreateOnSearch() {
      return this.settings[COMPANY_SETTINGS_ENTITY_KEYS.ALLOW_LABEL_CREATION]
    },

    lockedMessagesStates() {
      return getLockMessagesStates({
        isEdit: this.isEdit,
        modelValue: this.modelValue,
        isClosedParent: this.isClosedParent,
        contribute: this.formModel.contribute
      })
    },

    isShowElementDataSource() {
      return (
        this.isEdit &&
        !this.disableOnEdit &&
        !this.existContributeChildren &&
        this.dataSourceLimitations.isAvailable
      )
    }
  },

  watch: {
    RESULT_TYPE_OPTIONS: {
      handler(newValue, oldValue) {
        if (!isEmpty(newValue) && isEmpty(oldValue) && !this.formModel.fieldTypeId) {
          const [{ value }] = newValue
          this.formModel.fieldTypeId = value
        }
      },

      deep: true,
      immediate: true
    },

    automaticElementDatesForCreate: {
      handler(newValue) {
        if (newValue && !this.isEdit) {
          const { startDate, dueDate } = newValue
          this.formModel.automaticElementStartDate = startDate
          this.formModel.automaticDueDate = dueDate
        }
      },

      deep: true,
      immediate: true
    },

    'formModel.intervalId': {
      async handler() {
        if (this.areIntervalsLoaded && this.areParentObjectivesLoaded) {
          const promises = []

          const setParentObjectives = async () => {
            this.parentObjectives = await this.getParentObjectives()
          }

          if (this.isEdit) {
            promises.push(this.onParameterUpdate(OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.INTERVAL))
          }

          promises.push(setParentObjectives())

          await Promise.all(promises)
        }
      }
    },

    isLockedOnPercent: {
      handler(newValue) {
        if (newValue) {
          this.formModel.fieldTypeId = KR_VALUE_TYPES.START_TARGET
        }
      },

      immediate: true
    },

    'formModel.ownerId': async function ownerIdChanged() {
      if (this.areAssigneesLoaded) {
        this.groups = await this.getGroups()
      }
    },

    // 'modelValue.id': {
    //   async handler(newValue, oldValue) {
    //     if (this.isEdit) {
    //       this.getChildKrs()
    //       this.parentObjectives = await this.getParentObjectives()
    //       this.resultTypeSettingsUid = uid()
    //     }
    //   }
    // },

    async modelValue(newValue, oldValue) {
      const isOpenOtherMetricFromCurrent = newValue.id && newValue.id !== oldValue.id && this.isEdit

      if (isOpenOtherMetricFromCurrent) {
        this.forceUpdateMilestones()
        this.getChildKrs()
        this.parentObjectives = await this.getParentObjectives(null, newValue.parentId)
        this.resultTypeSettingsUid = uid()
      }

      if (newValue) {
        this.initCustomFieldsValues()
        this.saveModelValueToFormModel()
        this.saveFormModelLocal()

        if (isOpenOtherMetricFromCurrent) {
          await nextTick()
          await this.fetchData()
          this.reloadUsersListsInCustomFieldsHub()
        }
      }

      const { showChart } = this
      if (isRefreshChartDataNeed({ oldValue, newValue, showChart })) {
        this.refreshChartData()
      }
    },

    editDataAreSaving(newValue) {
      if (newValue) {
        this.$emit('saving-started')
      } else {
        this.$emit('saving-ended')
      }
    },

    chartTriggers: {
      handler(newValue) {
        const { isEdit } = this
        this.showChart = getShowChartStatus({ ...newValue, isEdit })
      },

      deep: true
    }
  },

  created() {
    this.saveModelValueToFormModel()
  },

  mounted() {
    this.fetchData().finally(() => {
      this.setupScrollToComments({
        target: this.$refs?.sidebarLayout?.getScrollContainer(),
        shouldScroll: this.okrPayload?.scrollToComments,
        router: this.$router,
        route: this.$route
      })
    })
  },

  methods: {
    refreshChartData() {
      this.$nextTick(() => {
        this.$refs.objectiveChart?.refreshData()
      })
    },

    onProgressValueUpdated({ updatedElementParameters, element }) {
      this.applyElementUpdates({ updatedElementParameters, element })
      this.updateActivities()
      this.refreshChartData()
    },

    onPeriodThresholdUpdated({ updatedElementParameters, element }) {
      this.applyElementUpdates({ updatedElementParameters, element })
      this.updateActivities()
      this.refreshChartData()
    },

    async onProgressHistoryUpdated({ updatedElementParameters, element }) {
      await this.applyElementUpdates({ updatedElementParameters, element })
      this.refreshChartData()
    },

    async applyElementUpdates({ updatedElementParameters, element }) {
      const isParentUpdated = updatedElementParameters.some(
        ({ elementId }) => elementId === this.formModel.parentId
      )

      this.updateChildrenParams(updatedElementParameters)

      if (element) {
        this.$emit('update:model-value', {
          ...this.modelValue,
          ...element
        })
        // uncomment if need current value input will be returned into form
        //this.$nextTick(() => {
        //  this.resultTypeSettingsUid = uid()
        //})
      }

      if (isParentUpdated) {
        this.parentObjectives = await this.getParentObjectives()
      }
    },

    async fetchData() {
      // enable loader for fields
      this.isParentObjectivesLoading = true
      this.assigneesAreLoading = true
      this.isTeamsLoading = true

      if (!this.isEdit) {
        this.setFocusOnTitle()
      }

      if (this.isEdit || this.createAnother) {
        this.isGroupManuallyChanged = true
        this.isOwnerManuallyChanged = true
      }

      const handleAssignees = async () => {
        this.assignees = await this.getAssignees(null, true)
      }

      const handleGroups = async () => {
        this.groups = await this.getGroups()
      }

      const handleParentObjectives = async () => {
        this.parentObjectives = await this.getParentObjectives()
      }

      const handleIntervals = async () => {
        this.intervals = await this.getIntervals()
      }

      const handleLabels = async () => {
        this.labels = await this.getLabels()
      }

      const handleLevels = async () => {
        this.levels = await this.getLevelsForFilter()
      }

      if (!this.isEdit) {
        await handleAssignees()
      }

      const promises = [
        handleParentObjectives(),
        handleIntervals(),
        handleLabels(),
        handleLevels(),
        handleGroups()
      ]

      if (this.isEdit) {
        promises.unshift(handleAssignees())
        promises.push(this.getChildKrs())
      }

      return Promise.all(promises).then(() => {
        this.initCustomFieldsValues()
        this.saveFormModelLocal()
        this.initialDataAreLoaded = true
      })
    },

    async expandAll() {
      this.isExpandAllLoading = true
      const objectivesApi = new ObjectivesApiHandler()

      try {
        const payload = createPayloadForExpandAllNestedItems({
          modelValue: this.modelValue,
          childrenOrder: this.childrenOrder
        })
        const { elements } = await objectivesApi.getObjectives(payload)
        this.childKrs = elements

        this.$refs.childKRs.toggleExpandAll()
        this.isExpandAllLoading = false

        // this.$refs.childKRs.toggleExpandAll()
      } catch (error) {
        handleError({ error })
      }
    },

    collapseAll() {
      this.$refs.childKRs.toggleExpandAll(false)
    },

    showFormField(isValueExist = false) {
      if (this.isEdit) {
        return this.editable ? true : isValueExist
      }
      return true
    },

    onParentObjectiveIdClick(item) {
      this.$emit('on-parent-objective-id-click', item)
    },

    expandItem({ parentId, items }) {
      const target = this.childKrs.find(item => item.id === parentId)
      if (target) {
        target.childElements = items
      }
    },

    async unlinkChildElement(childElement) {
      try {
        const objectivesApi = new ObjectivesInfoApiHandler()
        await objectivesApi.updateOkrElement({
          ...childElement,
          elementId: childElement.id,
          parentId: null
        })

        this.$emit('update')
        this.onChildUpdated()
      } catch (error) {
        handleError({ error })
      }
    },

    async onUpdateProgressAggregation(progressAggregationTypeId) {
      this.milestonesSettings.progressAggregationTypeId = progressAggregationTypeId
      await this.runWithFlagOkrParamSavingStatus(
        OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.MILESTONES,
        async () =>
          await this.updateKeyResult({
            ignoreMilestonesUpdate: false
          })
      )
    },

    async onMilestonesSettingsUpdate(milestonesSettings) {
      this.milestonesSettings = cloneDeep(milestonesSettings)

      await this.runWithFlagOkrParamSavingStatus(
        OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.MILESTONES,
        async () =>
          await this.updateKeyResult({
            ignoreMilestonesUpdate: false
          })
      )

      this.refreshChartData()
    },

    async changeResultType(resultType) {
      // console.log(this.formModel.fieldTypeId, resultType)
      const isSwitchToThreshold = isThresholdProgress({
        element: {
          fieldTypeId: resultType
        }
      })

      if (
        this.isEdit &&
        isSwitchToThreshold &&
        isNull(this.formModel[METRIC_VALUES_KEYS.THRESHOLD_CURRENT_VALUE])
      ) {
        // set it manually here to copy the current value to threshold current value in edit mode
        // because for threshold type default value is null
        // but it only can be set in create mode
        this.formModel[METRIC_VALUES_KEYS.THRESHOLD_CURRENT_VALUE] = this.currentValue
      }

      this.formModel.fieldTypeId = resultType
      await this.runWithFlagOkrParamSavingStatus(
        [
          OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.TYPE,
          OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.MILESTONES
        ],
        async () => {
          const newMilestonesSettings = await this.requestDataUpdateFromMilestones({
            fieldTypeId: resultType
          })
          this.milestonesSettings = cloneDeep(newMilestonesSettings)

          await this.updateKeyResult({
            ignoreMilestonesUpdate: false
          })

          if (this.isEdit) {
            const fieldTypeIdLabel = getProgressOptionByFieldTypeId(resultType).label

            tracker.logEvent('progress option changed', {
              progress_option: this.$t(fieldTypeIdLabel, 1, { locale: 'en' }).toLowerCase()
            })
          }
        }
      )
    },

    async onBinaryValueUpdate(value) {
      this.formModel[METRIC_VALUES_KEYS.BINARY_VALUE] = value
      this.binaryValueIsSaving = true
      await this.updateKeyResult()
      if (this.isEdit) {
        trackManualUpdateGradeEvent({
          confidenceLevel: this.formModel.confidenceLevelId,
          id: this.modelValue.id,
          issueType: TYPE_ID_NAMES[OBJECTIVE_TYPES.KR],
          source: EVENT_SOURCES.FORM,
          value: value * 100 // for convert boolean to percent
        })
      }
      this.binaryValueIsSaving = false
    },

    close({ checkDataChange = true, createdKR = null, updatedKR = null }) {
      this.$emit('close', { checkDataChange, createdKR, updatedKR })
    },

    updateChildrenParams(updatedElementParameters) {
      saveUpdatedChildParameters(this.childKrs, updatedElementParameters)
    },

    turnOffLoading() {
      if (this.loading) {
        this.loading = false
      }
    },

    async handleSaveKr({
      showLoader = true,
      closeForm = true,
      ignoreCreateAnotherStatus = false,
      ignoreMilestonesUpdate = true
    } = {}) {
      this.loading = showLoader

      const { saved, createdKeyResult } = await this.save({
        ignoreMilestonesUpdate
      })

      if (!saved) {
        this.turnOffLoading()
        return { saved, createdKeyResult }
      }

      if (!this.isEdit) {
        this.$emit('on-created', createdKeyResult)

        if (this.createAnother) {
          this.scheduleCreateAnother()
          this.setFocusOnTitle()
        }
      }

      if (!this.createAnother || ignoreCreateAnotherStatus) {
        if (closeForm) {
          this.close({
            createdKR: !this.isEdit ? createdKeyResult : null,
            checkDataChange: false
          })
        } else {
          this.$emit('update:model-value', createdKeyResult)
        }
      }

      this.turnOffLoading()

      return { saved, createdKeyResult }
    },

    validateName() {
      if (isStringEmpty(this.formModel.name)) {
        this.isTitleEmpty = true
        this.setFocusOnTitle()
        return false
      }
      return true
    },

    validate() {
      return (
        this.validateParentElement().isParentValidationPassed &&
        this.validateName() &&
        !this.isDescriptionTooLong
      )
    },

    /**
     * @private
     * This method is intended for internal use only.
     * Do not call this method directly; instead, use {@link handleSaveKr}.
     *
     * @function
     * @returns {Promise<{saved: boolean, createdKeyResult: null | {}}>}
     */
    async save({ ignoreMilestonesUpdate = true } = {}) {
      if (!this.validate()) {
        return { saved: false, createdKeyResult: null }
      }

      if (!this.isEdit && this.$refs.description.active) {
        this.$refs.description.blur()
      }

      let currentValue = this.currentValue

      if (!this.isThreshold) {
        currentValue = Number(currentValue)
      }

      if (this.isNumber && !this.isEdit) {
        currentValue = Number(this.originalValue)
      }

      const resolvedMilestonesSettings =
        this.isEdit && ignoreMilestonesUpdate ? {} : this.milestonesSettings

      const payload = {
        name: this.formModel.name.trim(),
        parentId: this.formModel.parentId,
        description: this.formModel.description,
        users: this.createUsersListForPayload(this.formModel),
        groups: createGroupsList(this.formModel.groups),
        fieldTypeId: this.formModel.fieldTypeId,
        originalValue: this.isBinary ? DEFAULT_BINARY_ORIGINAL_VALUE : +this.originalValue,
        currentValue,
        targetValue: this.isBinary ? DEFAULT_BINARY_TARGET_VALUE : +this.targetValue,
        workspaceId: this.formModel.workspaceId || this.workspaceId,
        dueDate: this.formModel.dueDate,
        elementStartDate: this.formModel.elementStartDate,
        confidenceLevelId: this.formModel.confidenceLevelId,
        intervalId: this.formModel.intervalId,
        levelId: this.formModel.levelId,
        labelIds: this.formModel.labelIds,
        startDateManual: this.formModel.startDateManual,
        dueDateManual: this.formModel.dueDateManual,
        contribute: this.formModel.contribute,
        private: this.modelValue.private,
        updateStatusForNestedElements: this.formModel.updateStatusForNestedElements,
        unit: this.formModel.unit,
        thresholdConditionTypeId: this.isThreshold ? this.formModel.thresholdConditionTypeId : null,
        ...resolvedMilestonesSettings
      }

      if (!this.isEdit) {
        payload.customFields = this.customFieldsValues
      }

      let createdKeyResult = null
      let updateData = null
      let isSuccessful = true

      try {
        const objectivesApi = new ObjectivesInfoApiHandler()
        if (!this.isEdit) {
          createdKeyResult = await objectivesApi.createOkrElement(payload)
          createdKeyResult = createdKeyResult.elements[0]

          const fieldTypeIdLabel = getProgressOptionByFieldTypeId(
            createdKeyResult[REQUEST_ENTITY_KEYS.FIELD_TYPE_ID]
          ).label

          const trackingPayload = {
            category: EVENT_CATEGORIES.OKR_MANAGEMENT,
            contribution: createdKeyResult.contribute ? 'Yes' : 'No',
            ent_id: createdKeyResult.displayId,
            tab: this.navigationTab,
            source: this.source,
            initial_value: createdKeyResult.originalValue,
            target_value: createdKeyResult.targetValue,
            parent: !isNull(createdKeyResult.parentId) ? createdKeyResult.parentId : '',
            progress_option: this.$t(fieldTypeIdLabel, 1, { locale: 'en' }).toLowerCase(),
            unit: createdKeyResult.unit
          }

          if (this.isThreshold) {
            const label = getConditionParameterById({
              conditionId: createdKeyResult[METRIC_VALUES_KEYS.THRESHOLD_CONDITION_TYPE_ID]
            })

            trackingPayload.threshold_condition = this.$t(label, 1, {
              locale: 'en'
            }).toLowerCase()

            if (resolvedMilestonesSettings[OKR_ELEMENT_KEYS.GRANULATION_ID]) {
              const { label } = findPeriodGranulationOptionsByTypeId({
                typeId: resolvedMilestonesSettings[OKR_ELEMENT_KEYS.GRANULATION_ID]
              })

              trackingPayload.threshold_split_period = this.$t(label, 1, {
                locale: 'en'
              }).toLowerCase()
            }

            if (resolvedMilestonesSettings[OKR_ELEMENT_KEYS.PROGRESS_AGGREGATION_TYPE_ID]) {
              const { trackingLabel } = findProgressAggregationOptionsByTypeId({
                typeId: resolvedMilestonesSettings[OKR_ELEMENT_KEYS.PROGRESS_AGGREGATION_TYPE_ID]
              })

              trackingPayload.threshold_progress_calculation = trackingLabel
            }
          }

          const { elementTargetPeriods = [] } = resolvedMilestonesSettings

          if (!isEmpty(elementTargetPeriods) && !this.isBinary) {
            trackingPayload.progress_setting = this.isThreshold ? 'split' : 'milestones'
          }

          tracker.logEvent('Created KR', trackingPayload)

          gtmTracker.logEvent(EVENT_NAMES.KR_CREATED)
        } else {
          payload.elementId = this.modelValue.id
          payload[OKR_ELEMENT_ENTITY_KEYS.TYPE_ID] = OBJECTIVE_TYPES.KR

          if (this.existContributeChildren) {
            // need to prevent rewriting current value by grade on chages
            delete payload.currentValue
          }

          updateData = await objectivesApi.updateOkrElement(payload)

          createdKeyResult = updateData.element
          const updatedElementParameters = updateData.updatedElementParameters

          const isParentUpdated = updatedElementParameters.some(
            ({ elementId }) => elementId === this.formModel.parentId
          )
          if (isParentUpdated) {
            this.parentObjectives = await this.getParentObjectives(null, this.formModel.parentId)
          }
        }
      } catch (error) {
        isSuccessful = false
        handleError({ error })
      }

      if (updateData && !currentUserCanReadObjective(updateData.element)) {
        this.close({ checkDataChange: false })
        throw new Error(this.$t('objective.you_have_no_rights'))
      }

      return { saved: isSuccessful, createdKeyResult }
    },

    async getAssignees(searchString = null, selectUser = false) {
      let result = []
      const api = new AssigneesInfoApiHandler()

      const { OWNER_ID } = OKR_ELEMENT_ENTITY_KEYS

      this.assigneesAreLoading = true
      const payload = createGetUsersPayload({
        workspaceId: this.workspaceId,
        searchString,
        userData: this.userData,
        isOwnerManuallyChanged: this.isOwnerManuallyChanged,
        isEdit: this.isEdit,
        formModel: this.formModel
      })

      try {
        const users = await api.getUsers(payload)

        result = [...users]

        if (
          !isEmpty(users) &&
          isNull(this.formModel[OWNER_ID]) &&
          selectUser &&
          !this.isOwnerManuallyChanged
        ) {
          this.formModel[OWNER_ID] = this.userData.userAccountId
        }

        if (!this.isEdit && isNull(this.formModel[OWNER_ID])) {
          this.formModel[OWNER_ID] = this.userData.userAccountId
        }

        this.$nextTick(() => {
          if (!this.areAssigneesLoaded) {
            this.localFormModel[OWNER_ID] = this.formModel[OWNER_ID]
          }
          this.areAssigneesLoaded = true
        })
      } catch (error) {
        handleError({ error })
      } finally {
        this.assigneesAreLoading = false
      }

      return result
    },

    async getGroups(searchString = null) {
      // if (this.isEdit) return;
      const api = new GlobalGroupsApiHandler()
      this.isTeamsLoading = true
      let result = []

      const { GROUP_IDS } = REQUEST_ENTITY_KEYS
      const { GROUPS } = OKR_ELEMENT_ENTITY_KEYS

      const { ownerId, groups: selectedGroups } = this.formModel

      try {
        const payload = createGetGroupsPayload({
          searchString,
          ownerId,
          selectedGroups,
          workspaceId: this.workspaceId,
          isGroupManuallyChanged: this.isGroupManuallyChanged,
          users: this.assignees
        })

        const groups = await api.getGroupsForFilter(payload)
        this.groups = groups
        this.isTeamsLoading = false

        if (!this.isEdit) {
          this.formModel[GROUPS] = payload[GROUP_IDS]
        }

        this.$nextTick(() => {
          if (!this.areGroupsLoaded) {
            this.localFormModel[GROUPS] = this.formModel[GROUPS]
          }
          this.areGroupsLoaded = true
        })

        result = groups
      } catch (error) {
        handleError({ error })
        this.isTeamsLoading = false
      }

      return result
    },

    async getLevelsForFilter() {
      const levelApi = new LevelApiHandler()
      let result = []
      try {
        this.isLevelsLoading = true
        result = await levelApi.getLevelsForFilter({
          workspaceId: this.workspaceId
        })
      } catch (error) {
        handleError({ error })
      }
      this.isLevelsLoading = false
      return result
    },

    async getLabels(searchString = null) {
      let result = []
      const api = new LabelsApiHandler()
      if (!searchString) {
        this.isLabelsLoading = true
      }
      try {
        result = await api.getLabels({ name: searchString })
      } catch (error) {
        handleError({ error })
      }
      this.isLabelsLoading = false
      return result
    },

    async createLabelOnSearch(searchString) {
      const api = new LabelsApiHandler()
      try {
        const label = await api.createLabel({ name: searchString })
        this.formModel.labelIds.push(label.id)
        this.labels.push(label)

        trackLabelCreatedEvent({
          source: EVENT_SOURCES.OKR_FORM,
          label: label.name
        })

        this.updateKeyResult()
        this.$refs.labelsSelect?.clearInput()
      } catch (error) {
        handleError({ error })
      }
    },

    async getParentObjectives(searchString = null, predefinedParentId = null) {
      const api = new ObjectivesInfoApiHandler()
      let result = []
      this.isParentObjectivesLoading = true

      let parentId = predefinedParentId || this.modelValue?.parentId

      if (isNull(parentId) || parentId === this.modelValue?.id) {
        parentId = undefined
      }

      const payload = {
        elementTypeId: OBJECTIVE_TYPES.KR,
        intervalId: this.formModel.intervalId,
        elementId: this.modelValue?.id || null,
        parentId,
        searchString,
        workspaceId: this.formModel.workspaceId || this.workspaceId
      }

      try {
        const { currentIndex, elements } = await api.getParentObjectiveInfo(payload)
        result = elements

        if (searchString === null && currentIndex === 0 && !elements.length) {
          this.formModel.parentId = DEFAULT_FORM_MODEL.parentId
          return result
        }

        if (this.formModel.parentId === -1) {
          this.formModel.parentId = DEFAULT_FORM_MODEL.parentId
        }

        this.$nextTick(() => {
          if (!this.areParentObjectivesLoaded) {
            this.localFormModel.parentId = this.formModel.parentId
          }
          this.areParentObjectivesLoaded = true
        })
      } catch (error) {
        handleError({ error })
      } finally {
        this.isParentObjectivesLoading = false
      }

      return result
    },

    async getIntervals() {
      const api = new IntervalsInfoApiHandler()
      let result = []
      this.isIntervalsLoading = true
      try {
        const { currentIndex, intervals } = await api.getIntervalsInfo({
          workspaceId: this.formModel.workspaceId || this.workspaceId
        })
        result = intervals

        if (!this.formModel.intervalId) {
          this.formModel.intervalId = intervals[currentIndex].id
        }

        this.$nextTick(() => {
          this.areIntervalsLoaded = true
        })
      } catch (error) {
        handleError({ error })
      } finally {
        this.isIntervalsLoading = false
      }
      return result
    },

    onGroupSelect() {
      this.isGroupManuallyChanged = true
    },

    onOwnerSelect() {
      this.isOwnerManuallyChanged = true
    },

    initCustomFieldsValues() {
      this.customFieldsValues = {
        ...createInitialCustomFieldsValuesForOkrElement({
          isEdit: this.isEdit,
          elementTypeId: OBJECTIVE_TYPES.KR,
          modelValueCustomFields: this.modelValue?.customFields,
          currentWorkspaceCustomFields: this.currentWorkspaceCustomFields
        })
      }
    },

    saveFormModelLocal() {
      this.localFormModel = cloneDeep(this.formModel)
    },

    scheduleCreateAnother() {
      this.restoreToDefaultsMilestones()
      this.resultTypeSettingsUid = uid()
      this.formModel.name = ''
      this.formModel.description = ''

      Object.values(METRIC_VALUES_KEYS).forEach(key => {
        if (has(this.formModel, key)) {
          this.formModel[key] = DEFAULT_RESULT_TYPE_VALUES[key]
        }
      })
    },

    cancelDescriptionEditing() {
      if (this.isEdit) {
        this.formModel.description = this.modelValue.description
      }
    },

    /**
     * @param {number|null} levelId
     * @param {number} typeId
     * @param {boolean} isEdit - Indicates whether the operation is in edit mode.
     *   @note This parameter should be explicitly passed as an argument to avoid
     *   relying on `this.isEdit`, as it can be modified by calling the `onSaveButtonClick`
     *   method. Knowing whether we are in edit mode before the element is saved is crucial,
     *   as this determines if the element has already been saved and we are indeed in edit mode.
     */
    handleOpenChildElement({
      levelId = null,
      typeId = OBJECTIVE_TYPES.PERSONAL,
      isEdit = false
    } = {}) {
      let parameters = {
        parentId: this.modelValue.id,
        intervalId: this.modelValue.intervalId,
        workspaceId: this.workspaceId,
        levelId
      }

      const isMetric = typeId === OBJECTIVE_TYPES.KR

      if (isMetric) {
        parameters = {
          ...parameters,
          intervalStartDate: this.modelValue.intervalStartDate,
          intervalEndDate: this.modelValue.intervalEndDate
        }
      }

      if (!isEdit) {
        parameters = {
          ...parameters,
          formView: isMetric ? OKR_FORM_VIEWS.KR : OKR_FORM_VIEWS.OBJECTIVE
        }
      }

      const [emitEvent] = [
        isEdit && !isMetric && ADD_NESTED_ELEMENT_EMIT_EVENTS.OPEN_CHILD_OBJECTIVE,
        isEdit && isMetric && ADD_NESTED_ELEMENT_EMIT_EVENTS.OPEN_KR,
        !isEdit && ADD_NESTED_ELEMENT_EMIT_EVENTS.CREATE_NESTED_WHILE_CREATE_CURRENT
      ].filter(Boolean)

      if (emitEvent) {
        this.$emit(emitEvent, parameters)
      }

      if (emitEvent === ADD_NESTED_ELEMENT_EMIT_EVENTS.OPEN_CHILD_OBJECTIVE) {
        this.updateActivities()
      }
    },

    async onContributeChange() {
      await this.runWithFlagOkrParamSavingStatus(
        OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.CONTRIBUTE,
        this.updateKeyResult
      )
    },

    async updateKeyResult({ ignoreMilestonesUpdate = true } = {}) {
      if (this.initialDataAreLoaded && this.isEdit && this.areDataChanged) {
        // wait for refs after result type change
        try {
          await this.$nextTick()
          await this.handleSaveKr({ closeForm: false, ignoreMilestonesUpdate })
          this.saveFormModelLocal()
          this.updateActivities()
        } catch (error) {
          handleError({ error })
        }
      }
    },

    async onNameUpdate() {
      if (this.editable && this.validateName()) {
        await this.runWithFlagOkrParamSavingStatus(
          OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.NAME,
          this.updateKeyResult
        )
      }
    },

    async onThresholdConditionUpdate() {
      if (this.editable) {
        this.thresholdConditionIsSaving = true
        await this.updateKeyResult()
        this.thresholdConditionIsSaving = false
      }
    },

    async onOriginalValueUpdate() {
      if (
        this.isEdit &&
        notEqualStringifiedValues(this.originalValue, this.modelValue.originalValue)
      ) {
        this.originalValueIsSaving = true
        await this.updateKeyResult()
        this.originalValueIsSaving = false
      }
    },

    async onCurrentValueUpdate() {
      if (
        this.isEdit &&
        notEqualStringifiedValues(this.currentValue, this.modelValue.currentValue)
      ) {
        this.currentValueIsSaving = true
        await this.updateKeyResult()
        trackManualUpdateGradeEvent({
          confidenceLevel: this.formModel.confidenceLevelId,
          id: this.modelValue.id,
          issueType: TYPE_ID_NAMES[OBJECTIVE_TYPES.KR],
          source: EVENT_SOURCES.FORM,
          value: this.currentValue
        })
        this.currentValueIsSaving = false
      }
    },

    async onTargetValueUpdate() {
      if (this.isEdit && notEqualStringifiedValues(this.targetValue, this.modelValue.targetValue)) {
        this.targetValueIsSaving = true
        await this.updateKeyResult()
        this.targetValueIsSaving = false
      }
    },

    async onUnitUpdate() {
      if (this.isEdit && this.isUnitChanged) {
        await this.runWithFlagOkrParamSavingStatus(
          OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.UNIT,
          this.updateKeyResult
        )
      }
    },

    onUpdateElementDate(params) {
      const { val, dateProp, savingStatus } = params

      this.formModel[dateProp] = val

      this.onParameterUpdate(savingStatus)
    },

    updatePeriodMode(mode) {
      this.formModel.startDateManual = mode
      this.formModel.dueDateManual = mode
      if (mode) {
        this.formModel.elementStartDate = this.formModel.automaticElementStartDate
        this.formModel.dueDate = this.formModel.automaticDueDate
      } else {
        this.formModel.elementStartDate = null
        this.formModel.dueDate = null
      }

      this.onParameterUpdate(OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.PERIOD_MODE)
    },

    openCheckinModal(payload) {
      this.handleOpenCheckinModal(payload)
    },

    onCheckinSaved({ updatedElementParameters, element }) {
      this.applyElementUpdates({
        updatedElementParameters,
        element: getSafeUpdatedElementParameters({ element })
      })
      this.updateActivities()
      this.refreshChartData()
    },

    async unlinkParentElement() {
      this.formModel.parentId = DEFAULT_FORM_MODEL.parentId

      if (this.isEdit) {
        await this.runWithFlagOkrParamSavingStatus(
          OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.PARENT,
          async () => {
            await this.onParentObjectiveUpdate(DEFAULT_FORM_MODEL.parentId)
          }
        )
      }
    },

    async onParentObjectiveUpdate(value) {
      await this.runWithFlagOkrParamSavingStatus(
        OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.PARENT,
        async () => {
          await this.updateKeyResult()
          this.localFormModel.parentId = value
        }
      )
    },

    onUpdateInterval(intervalId) {
      if (this.formModel.elementStartDate || this.formModel.dueDate) {
        this.temporaryIntervalId = intervalId
        this.isConfirmIntervalChangeShow = true
      } else {
        this.formModel.intervalId = intervalId
      }
    },

    hideConfirmIntervalChange() {
      this.isConfirmIntervalChangeShow = false
      this.temporaryIntervalId = null
    },

    onConfirmIntervalChange() {
      this.formModel.elementStartDate = null
      this.formModel.dueDate = null
      this.formModel.intervalId = this.temporaryIntervalId
      this.hideConfirmIntervalChange()
    },

    async saveDescription() {
      if (!this.descriptionWasChanged) {
        this.descriptionWasChanged = true
      }
      await this.runWithFlagOkrParamSavingStatus(
        OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.DESCRIPTION,
        this.updateKeyResult
      )
    },

    onDatesTriggerClick() {
      if (!this.isFieldDisabled) {
        this.showDatesDropdown = !this.showDatesDropdown
      }
    },

    async onParameterUpdate(savingStatus) {
      await this.runWithFlagOkrParamSavingStatus(savingStatus, this.updateKeyResult)

      // emits only if level was changed
      // to update OKR icon in breadcrumbs
      if (savingStatus === OKR_ELEMENT_PARAMETERS_SAVING_STATUSES.TYPE) {
        this.$emit('level-changed', this.formModel.levelId)

        this.updateResultType()
      }
    },

    updateResultType() {
      if (!this.isEdit) {
        this.$nextTick(() => {
          const allAvailableProgressOptions = this.RESULT_TYPE_OPTIONS.map(({ value }) => value)

          if (!allAvailableProgressOptions.includes(this.formModel.fieldTypeId)) {
            this.formModel.fieldTypeId = allAvailableProgressOptions[0]
          }
        })
      }
    },

    async onUpdateCustomFieldValue({ fieldId, value }) {
      this.customFieldsValues[fieldId] = value
    },

    async getChildKrs({ isRefetch = false, isUpdateHistory = false } = {}) {
      try {
        const objectivesApi = new ObjectivesInfoApiHandler()
        const { items, weightManual } = await objectivesApi.getChildObjectives({
          parentId: this.modelValue.id,
          workspaceId: this.modelValue.workspaceId,
          // intervalIds: [this.modelValue.intervalId],
          searchType: SEARCH_TYPES.OKR_EXPLORER,
          order: this.childrenOrder,
          childOrder: this.childrenOrder,
          offset: 0,
          limit: 1000
        })
        this.childKrs = items
        this.weightIsManual = weightManual
        if (isUpdateHistory) {
          this.updateActivities()
        }
        if (isRefetch) {
          this.$refs.childKRs.refetchExpandedElementsChildItems()
        }
      } catch (error) {
        handleError({ error })
      }
    },

    okrElementToLinkIssuePayload,
    okrElementToLinkWorkItemPayload,

    onCreateWorkItemForNestedElement(parentElement) {
      this.startWorkItemCreation(parentElement, this.firstWorkItemKindId)
    },

    onActionMenuItemsClick(actionId) {
      if (!this.allowCreateElements) return

      const { isTaskAction, typeId, levelId, isWorkItemAction } = this.getPayloadForHandleAction({
        actionId
      })

      if (isTaskAction) {
        const actionHandlers = {
          [OKR_ELEMENT_FORM_MENU_ACTIONS.LINK_TASK]: this.onLinkJiraIssueClick,
          [OKR_ELEMENT_FORM_MENU_ACTIONS.CREATE_JIRA_ISSUE]: this.onCreateJiraIssueClick
        }

        actionHandlers[actionId]?.()
        return
      }

      if (isWorkItemAction) {
        const actionHandlers = {
          [OKR_ELEMENT_FORM_MENU_ACTIONS.LINK_WORK_ITEM]: this.onLinkWorkItemClick,
          [OKR_ELEMENT_FORM_MENU_ACTIONS.CREATE_WORK_ITEM]: this.onCreateWorkItemClick
        }

        if (!actionHandlers[actionId]) {
          this.onCreateWorkItemClick(actionId)
          return
        }

        actionHandlers[actionId]()

        return
      }

      this.startCreateNestedElement({
        levelId,
        typeId
      })
    },

    onCreateJiraIssueFormSave() {
      this.getChildKrs({ isRefetch: true })
      this.$emit('update')
      if (this.isEdit) {
        this.updateActivities()
      }
    },

    onCreateWorkItemFormSave() {
      this.getChildKrs({ isRefetch: true })
      this.$emit('update')
      if (this.isEdit) {
        this.updateActivities()
      }
    },

    async onLinkJiraIssueClick() {
      if (!this.isEdit) {
        const { saved } = await this.handleSaveKr({
          showLoader: false,
          closeForm: false,
          ignoreCreateAnotherStatus: true
        })

        if (!saved) return
      }

      this.$emit('link-jira-issue', okrElementToLinkIssuePayload(this.modelValue))
    },

    async onLinkWorkItemClick() {
      if (!this.isEdit) {
        const { saved } = await this.handleSaveKr({
          showLoader: false,
          closeForm: false,
          ignoreCreateAnotherStatus: true
        })

        if (!saved) return
      }

      this.$emit('link-work-item', okrElementToLinkWorkItemPayload(this.modelValue))
    },

    async onCreateWorkItemClick(workItemId) {
      const _workItemId = workItemId ?? this.firstWorkItemKindId

      if (!_workItemId) return

      if (!this.isEdit) {
        const { saved } = await this.handleSaveKr({
          showLoader: false,
          closeForm: false,
          ignoreCreateAnotherStatus: true
        })

        if (!saved) return
      }

      this.startWorkItemCreation(okrElementToCreateWorkItemPayload(this.modelValue), _workItemId)
    },

    async onCreateJiraIssueClick() {
      if (!this.isEdit) {
        const { saved } = await this.handleSaveKr({
          showLoader: false,
          closeForm: false,
          ignoreCreateAnotherStatus: true
        })

        if (!saved) return
      }

      this.createJiraIssue(okrElementToCreateIssuePayload(this.modelValue))
    },

    async startCreateNestedElement({ levelId = null, typeId = OBJECTIVE_TYPES.PERSONAL } = {}) {
      const parameters = {
        levelId,
        typeId,
        isEdit: this.isEdit
      }

      if (!this.isEdit) {
        const { saved } = await this.handleSaveKr({
          showLoader: false,
          closeForm: false,
          ignoreCreateAnotherStatus: true
        })

        if (!saved) return
      }

      // wait until modelValue is updated
      await this.$nextTick()
      this.handleOpenChildElement(parameters)
    },

    onChildUpdated() {
      this.getChildKrs({ isRefetch: true })
      this.updateElementAndDependents()
    },

    updateElementAndDependents() {
      this.$emit('update')
      if (this.isEdit) {
        this.updateActivities()
      }
    },

    saveModelValueToFormModel() {
      if (this.modelValue) {
        this.formModel = {
          ...this.formModel,
          ...this.modelValue,
          name: this.modelValue.name || DEFAULT_FORM_MODEL.name,
          fieldTypeId:
            this.modelValue.fieldTypeId ||
            this.formModel.fieldTypeId ||
            DEFAULT_FORM_MODEL.fieldTypeId,

          unit: this.modelValue.unit || DEFAULT_FORM_MODEL.unit,
          originalValue: this.modelValue.originalValue ?? DEFAULT_FORM_MODEL.originalValue,
          currentValue: this.modelValue.currentValue ?? DEFAULT_FORM_MODEL.currentValue,
          targetValue: this.modelValue.targetValue ?? DEFAULT_FORM_MODEL.targetValue,
          thresholdConditionTypeId:
            this.modelValue.thresholdConditionTypeId || DEFAULT_FORM_MODEL.thresholdConditionTypeId,

          description: this.modelValue.description || DEFAULT_FORM_MODEL.description,
          parentId: this.modelValue.parentId || DEFAULT_FORM_MODEL.parentId,
          workspaceId: this.modelValue.workspaceId || DEFAULT_FORM_MODEL.workspaceId,

          automaticConfidenceLevelId:
            this.modelValue.automaticConfidenceLevelId ||
            DEFAULT_FORM_MODEL.automaticConfidenceLevelId,

          intervalId: this.modelValue.intervalId || DEFAULT_FORM_MODEL.intervalId,
          levelId: this.modelValue.levelId || DEFAULT_FORM_MODEL.levelId,

          ...convertUsersListToOwnerAndStakeholders(this.modelValue.users),
          groups: [...convertGroupsListToGroups(this.modelValue.groups)],
          labelIds: this.modelValue.labels?.map(label => label.id) || [],
          confidenceLevelId:
            this.modelValue.confidenceLevelId || DEFAULT_FORM_MODEL.confidenceLevelId,

          dueDate: this.modelValue.dueDate || null,
          elementStartDate: this.modelValue.elementStartDate || null,

          automaticElementStartDate: this.modelValue.automaticElementStartDate,

          automaticDueDate: this.modelValue.automaticDueDate,
          contribute: isUndefined(this.modelValue.contribute)
            ? DEFAULT_FORM_MODEL.contribute
            : this.modelValue.contribute,

          private: isUndefined(this.modelValue.private)
            ? DEFAULT_FORM_MODEL.private
            : this.modelValue.private
        }

        if (this.isEdit) {
          const values = getResultTypeValues(this.modelValue)
          this.formModel = {
            ...this.formModel,
            ...values
          }

          this.saveMilestonesSettingsFromFormModel()
          this.isTitleEmpty = !this.validateName()
        }
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.krf-KeyResultForm {
  min-height: 0;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  // margin-top: 14px;
  height: 100%;
}

.krf-Activities {
  margin-top: 28px;
}

.krf-Title {
  margin-bottom: 8px;
}

.krf-ResultTypeWrapper {
  margin-bottom: 8px;

  &-binary {
    --rts-container-padding: 0;
  }
}

.krf-ResultTypeWrapper_LockMessageDescription {
  font-weight: fw('regular');
}

.krf-Progress {
  // margin-bottom: 22px;
}

.krf-Progress_NavButton {
  color: $dark-2;
}

// .krf-ChildKeyResultsHeader {
//   margin-top: 28px;
//   // avoid 'jumping' when type of field content(empty & selected) is changed
//   min-height: 72px;
// }

// .krf-MenuName {
//   margin-left: 16px;
//   font-size: $fs-16;
// }

:deep(.kr-ObjectiveForm-NameField:first-child) {
  background-color: $white;
  border: 1px solid $azure-medium;
  border-radius: $border-radius-md;

  &:hover {
    background-color: $azure-medium;
  }
}

.krf-DatesLabel_Text {
  font-weight: fw('regular');
  font-size: $fs-14;
  align-self: flex-start;
}

.krf-DatesLabel_Icon {
  margin-right: auto;
  align-self: flex-start;
}

.krf-DatesLabel {
  display: flex;
  align-items: center;
  gap: 4px;
}

.krf-DatesLabel_Value {
  width: auto;
  flex: 1;
  text-align: right;
  .is-safari & {
    // Fix for safari breaking wbr
    line-height: 20px;
  }
}

.krf-Sidebar {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.krf-NoPermissionsMessage {
  margin-bottom: 26px;
  --icon-color: #{$grade-medium-color-next};
}

.krf-ClosedStatusMessage {
  margin-bottom: 26px;
}

.krf-ClosedStatusMessage_Description {
  font-weight: fw('regular');
}

.krf-LockMessage {
  margin: 0 0 8px 0;
}
</style>

<style lang="scss">
.krf-KeyResultForm {
  .o-confirm-actions {
    margin-top: 24px;
  }

  .fdf-Label {
    font-size: $fs-12;
    line-height: 14px;
    padding-bottom: 4px;
  }
}

.krf-ParentObjectiveWrapper {
  margin-bottom: 16px;

  .as-AppDroplistButton_Icon-prepend {
    margin-right: 0;
  }
}
</style>
